import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Tabs from '../components/Tabs';
import Icon from '../components/Icons';
import './DealWorkspace.css';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';
import { useAuth } from '../AuthContext';
import { useOrg, createInvite } from '../data/orgs';
import { useEntityDoc, useEntityList, createEntity, updateEntity, deleteEntity } from '../data/entities';
import { useDealPowers, useVisibleDeals, DEFAULT_DEAL_ROLE } from '../data/permissions';
import { db as fsdb } from '../firebaseConfig';
import { downloadAsset } from '../data/uploadService';
import DealPointsRegister from '../components/DealPointsRegister';
import VersionRail from '../components/VersionRail';
import { recordRecentDeal } from '../components/Sidebar';
import {
  DP_DECISION_LABELS,
  decisionOf,
  decisionsSince,
  CATEGORY_LABELS,
  PARTY_COLORS,
  MANIFEST_SOURCE_LABEL,
  FACT_TYPE_LABEL,
  projectTypeOf,
  isNegotiated,
  isSupersededDoc,
  statusLabel,
  appendAudit,
  actorOf,
  acceptSuggestedPoint,
  cabinetOf,
  docViewOf,
} from '../data/deals';
import Redline from '../components/Redline';
import { aiDocAgent, aiWorkbookAgent, aiProposeAnswers, aiPurposeAudit, aiStructureMap, aiDraftEmail, isAiConfigured, downloadWordExport, downloadPackageExport, createPackageShareLink, samCompliance, samDraftResponse } from '../data/legalApi';
import { computeFactConflicts } from '../data/factReview';
import { useCollection, useDoc } from '../data/firestore';
import TaskProgress from '../components/TaskProgress';
import FitText from '../components/FitText';
import { HelpDot, Term } from '../components/GuidePanel';
import SpotlightTour from '../components/SpotlightTour';
import ScrollZone from '../components/ScrollZone';
import { CommandPanel, openCommandBar } from '../components/CommandBar';
import { UploadDocModal, DocTagPicker } from '../components/AddDocumentPortal';
import EnvelopeBuilder from '../components/EnvelopeBuilder';
import CertificateModal from '../components/CertificateModal';
import KnowledgeTab from '../components/KnowledgeTab';
import { signResend, listSecureShares, revokeSecureShare } from '../data/legalApi';
import { getAssetDownloadUrl } from '../data/uploadService';

const DOC_STATUS_TONE = { queued: 'b-info', processing: 'b-info', ready: 'b-ok', error: 'b-danger' };

// ---- Signature envelopes -------------------------------------------------------------
const ENV_STATUS_TONE = {
  queued: 'b-info', sent: 'b-info', completing: 'b-info', completed: 'b-ok', voided: 'b-muted', error: 'b-danger', draft: 'b-muted',
};
const SIGNER_GLYPH = { pending: '·', notified: '✉', viewed: '👁', signed: '✓' };

function EnvelopesSection({ scope, participant }) {
  const { user } = useAuth();
  const toast = useToast();
  const { can } = useDealPowers(scope.deal);
  const { docs: envelopes } = useEntityList('envelope', scope);
  const [busyKey, setBusyKey] = useState('');
  const live = (envelopes || []).filter((e) => e.status !== 'draft');
  if (!live.length) return null;

  const resend = async (env, signer) => {
    setBusyKey(`${env.id}:${signer.key}`);
    try {
      await signResend({ orgId: scope.org, dealId: scope.deal, envelopeId: env.id, signerKey: signer.key });
      toast(`Signing link re-sent to ${signer.name}`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusyKey('');
    }
  };

  const voidEnvelope = async (env) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Void "${env.title}"? Signing links stop working immediately.`)) return;
    try {
      await updateEntity('envelope', scope, env.id, { status: 'voided', voidedBy: user.uid });
      await appendAudit(scope, actorOf(user, participant), 'envelope.voided', `Signature envelope "${env.title}" voided`, { envelopeId: env.id });
      toast('Envelope voided', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const openArtifact = async (key) => {
    try {
      const url = await getAssetDownloadUrl({ key });
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <span className="u-muted u-small">Signature envelopes — who has signed what, with sealed evidence.</span>
      {live.map((env) => (
        <div key={env.id} className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <div className="u-split">
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
              <strong>{env.title}</strong>
              <span className={`cs-badge ${ENV_STATUS_TONE[env.status] || 'b-info'}`}>{env.status}</span>
              <span className="u-xs u-muted">{(env.docs || []).length} doc{(env.docs || []).length === 1 ? '' : 's'}</span>
            </span>
            {can('edit') && ['queued', 'sent', 'error'].includes(env.status) && (
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => voidEnvelope(env)}>Void</button>
            )}
          </div>
          {env.status === 'error' && (
            <div className="u-small" style={{ color: 'var(--danger)' }}>
              Send failed: {env.sendError || 'unknown error'}{' '}
              {can('edit') && (
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => updateEntity('envelope', scope, env.id, { status: 'queued' })}>Retry send</button>
              )}
            </div>
          )}
          <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            {(env.signers || []).map((s) => (
              <span key={s.key} className={`cs-badge ${s.status === 'signed' ? 'b-ok' : 'b-info'}`} data-tip={`${s.email} · ${s.status}${s.signedAt ? ` ${new Date(s.signedAt).toLocaleString()}` : ''}`}>
                {SIGNER_GLYPH[s.status] || '·'} {s.name}
                {can('edit') && env.status === 'sent' && s.status !== 'signed' && s.status !== 'pending' && (
                  <button
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    style={{ marginLeft: 4, padding: '0 4px' }}
                    disabled={busyKey === `${env.id}:${s.key}`}
                    onClick={() => resend(env, s)}
                    data-tip="Re-send this signer's link (the old link stops working)"
                  >
                    ↻
                  </button>
                )}
              </span>
            ))}
          </div>
          {env.status === 'completed' && env.executed && (
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              {Object.entries(env.executed.byDoc || {}).map(([docId, a]) => (
                <button key={docId} className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => openArtifact(a.s3Key)}>
                  ⚖️ {a.title || 'Executed copy'}
                </button>
              ))}
              {env.executed.certS3Key && (
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => openArtifact(env.executed.certS3Key)}>
                  Certificate of completion
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- Documents tab -----------------------------------------------------------------
// Agentic document creation — describe the document (FDD, spec, agreement…)
// and the drafting agent authors a full structured v1: heading hierarchy,
// tables, TOC-ready. Iterate afterwards via the drafting agent on the doc page.
function DraftWithAiModal({ scope, deal, initialKind = 'document', onClose, onCreated }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const [kind, setKind] = useState(initialKind); // 'document' | 'deck' | 'workbook'
  // Knowledge seeding: ground the draft in related project brains — the tag
  // group (e.g. everything tagged with this company) or the whole org.
  const [seed, setSeed] = useState('none'); // 'none' | 'tags' | 'org'
  const [busy, setBusy] = useState(false);
  const dealTags = deal?.tags || [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (kind === 'workbook') {
        const r = await aiWorkbookAgent({
          orgId: scope.org, dealId: scope.deal, title: title.trim(), instruction,
        });
        toast(`"${r.title}" built — ${r.stats?.sheetCount || 0} sheets, ${r.stats?.formulaCount || 0} formulas`, 'ok');
        onCreated(r.docId);
        return;
      }
      const r = await aiDocAgent({
        orgId: scope.org, dealId: scope.deal, title: title.trim(), instruction,
        ...(kind === 'deck' ? { docType: 'deck' } : {}),
        ...(seed === 'tags' ? { seedTags: dealTags } : {}),
        ...(seed === 'org' ? { seedScope: 'org' } : {}),
      });
      toast(`"${r.title}" drafted — ${r.sections} ${kind === 'deck' ? 'slides' : 'sections'}`, 'ok');
      onCreated(r.docId);
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft with AI" onClose={onClose} maxWidth={640}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <div className="cs-viewmode" role="group" aria-label="What to draft">
          {[['document', 'Document'], ['deck', 'Presentation'], ['workbook', 'Workbook']].map(([k, label]) => (
            <button key={k} type="button" className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              {label}
            </button>
          ))}
        </div>
        <div className="cs-field">
          <label htmlFor="ai-doc-title">Working title (optional)</label>
          <input
            id="ai-doc-title" className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder={kind === 'deck' ? 'e.g. fast.legal — Investor Overview' : kind === 'workbook' ? 'e.g. Meridian MSA — 3-Year Fee & Margin Model' : 'e.g. Payments Service — Functional Design Document'}
          />
        </div>
        <div className="cs-field">
          <label htmlFor="ai-doc-brief">{kind === 'deck' ? 'Describe the presentation' : kind === 'workbook' ? 'Describe the model or analysis' : 'Describe the document'}</label>
          <textarea
            id="ai-doc-brief"
            className="cs-input"
            rows={7}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder={kind === 'deck'
              ? 'Audience, the story arc, key slides you want, any tables (pricing, roadmap, metrics)… The agent drafts a full deck — title slide, section dividers, bullets, speaker notes; you iterate from there and export to PowerPoint.'
              : kind === 'workbook'
                ? 'What to model and the key drivers — e.g. "36-month SaaS operating model: seat-based revenue with monthly growth and churn assumptions, headcount plan, gross margin, cash runway; summary sheet with ARR, burn and runway." The agent builds a real .xlsx: Assumptions sheet (blue inputs), formula-driven calc sheets, summary outputs — investor formatting throughout.'
                : 'What it covers, the audience, key sections you want, any tables (APIs, error codes, requirements)… The agent drafts a full structured document with headings, subsections and tables; you iterate from there.'}
          />
        </div>
        {kind !== 'workbook' && (
        <div className="cs-field">
          <label htmlFor="ai-doc-seed">Draw on existing knowledge</label>
          <select id="ai-doc-seed" className="cs-input" value={seed} onChange={(e) => setSeed(e.target.value)}>
            <option value="none">None — draft from the brief alone</option>
            {dealTags.length > 0 && (
              <option value="tags">Related projects ({dealTags.map((t) => `#${t}`).join(' ')})</option>
            )}
            <option value="org">Everything in this organization</option>
          </select>
          <div className="cs-help">
            The agent plans targeted retrievals across the selected project brains (documents, deal points,
            correspondence — plus a second hop for defined terms) and grounds the draft in what it finds.
            Every source excerpt is recorded in the version manifest.
          </div>
        </div>
        )}
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !instruction.trim()}>
            {busy ? 'Drafting (may take a minute)…' : 'Draft it'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Refactor an existing agreement into a derived agreement for a related
// entity (HoldCo OA → wholly-owned OpCo OA…). Queues the two-pass transform
// server-side; the new document lands with v1 = source baseline and v2 = the
// derived draft, so redline/clean/side-by-side and Word export work as usual.
function RefactorWithAiModal({ scope, sourceDoc, onClose, onQueued }) {
  const toast = useToast();
  const { deals: orgDeals } = useVisibleDeals();
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const [targetDealId, setTargetDealId] = useState(scope.deal);
  const [busy, setBusy] = useState(false);
  // Step 2: the entity cast extracted from the source — each role the derived
  // agreement must fill, with the agent's proposal from the brief. Rows the
  // brief didn't determine are flagged and must be filled before queueing.
  const [cast, setCast] = useState(null);

  const review = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await aiDocAgent({
        orgId: scope.org,
        dealId: scope.deal,
        deriveInterview: { sourceDocId: sourceDoc.id, instruction },
      });
      setCast((r.cast || []).map((s) => ({ ...s, value: s.proposed || '' })));
    } catch (err) {
      toast(err.message, 'danger');
    }
    setBusy(false);
  };

  const queue = async (confirmedCast) => {
    setBusy(true);
    try {
      const castText = (confirmedCast || [])
        .map((s) => `- ${s.role}: ${s.value}${s.source ? ` (in the source: ${s.source})` : ''}`)
        .join('\n');
      const r = await aiDocAgent({
        orgId: scope.org,
        dealId: scope.deal,
        derive: {
          sourceDocId: sourceDoc.id,
          title: title.trim(),
          instruction: castText
            ? `${instruction}\n\nConfirmed entity cast (authoritative — never contradict it):\n${castText}`
            : instruction,
          targetDealId,
          entityCast: (confirmedCast || []).map(({ role, source, value }) => ({ role, source, value })),
        },
      });
      toast('Refactor queued — the agent is planning and redrafting (a few minutes for a large agreement)', 'ok');
      onQueued(r.dealId, r.docId);
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };

  const confirmCast = () => {
    const missing = cast.filter((s) => s.needsInput && !s.value.trim());
    if (missing.length) {
      toast(`Fill in: ${missing.map((s) => s.role).join(', ')}`, 'danger');
      return;
    }
    queue(cast.filter((s) => s.value.trim()));
  };

  if (cast) {
    return (
      <Modal title="Confirm the entity cast" onClose={onClose} maxWidth={680}>
        <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
          <div className="u-small u-muted">
            These are the roles the agent found in <strong>{sourceDoc.title}</strong>. Confirm who plays each one
            in the new agreement — the transform treats your answers as authoritative and will never guess a
            party. Rows marked <span className="cs-badge b-warn">confirm</span> could not be inferred from your brief.
          </div>
          <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
            {cast.map((s, i) => (
              <div key={`${s.role}-${i}`} className="cs-field" style={{ margin: 0 }}>
                <label className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                  {s.role}
                  {s.needsInput && !s.value.trim() && <span className="cs-badge b-warn">confirm</span>}
                </label>
                <input
                  className="cs-input"
                  value={s.value}
                  placeholder={s.source ? `in the source: ${s.source}` : 'e.g. Netiva Holdings, LLC'}
                  onChange={(e) => setCast((c) => c.map((row, j) => (j === i ? { ...row, value: e.target.value } : row)))}
                />
                <span className="u-xs u-muted">
                  {s.source ? `Source: ${s.source}` : ''}{s.source && s.note ? ' · ' : ''}{s.note || ''}
                </span>
              </div>
            ))}
          </div>
          <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="cs-btn cs-btn-ghost" onClick={() => setCast(null)} disabled={busy}>Back</button>
            <button type="button" className="cs-btn cs-btn-primary" onClick={confirmCast} disabled={busy}>
              {busy ? 'Queueing…' : 'Refactor with this cast'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Refactor with AI" onClose={onClose} maxWidth={640}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={review}>
        <div className="u-small u-muted">
          Source: <strong>{sourceDoc.title}</strong> (current version). The agent studies the whole agreement —
          parties, members, shareholding, defined terms — plans what each section needs (keep, adapt, remove,
          add, with rationale), then redrafts. You get a clean draft AND a redline against the source, with every
          change traceable in the manifest.
        </div>
        <div className="cs-field">
          <label htmlFor="rf-title">New document title</label>
          <input
            id="rf-title" className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Netiva, LLC — Operating Agreement" autoFocus
          />
        </div>
        <div className="cs-field">
          <label htmlFor="rf-brief">Transformation brief</label>
          <textarea
            id="rf-brief" className="cs-input" rows={6} value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="e.g. Refactor this HoldCo operating agreement for the wholly-owned operating subsidiary Netiva, LLC: sole member is Netiva Holdings, LLC; adapt the member/shareholding provisions to single-member; IP stays at HoldCo (license down if needed); the OpCo is the revenue vehicle with employees and contractors…"
          />
        </div>
        <div className="cs-field">
          <label htmlFor="rf-target">Create in project</label>
          <select id="rf-target" className="cs-input" value={targetDealId} onChange={(e) => setTargetDealId(e.target.value)}>
            {(orgDeals || []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}{d.id === scope.deal ? ' (this project)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button" className="cs-btn cs-btn-ghost" disabled={busy || !instruction.trim()}
            title="Skip the entity review — the agent will flag anything ambiguous as open questions on the finished draft"
            onClick={() => queue([])}
          >
            Queue without review
          </button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !instruction.trim()}>
            {busy ? 'Reading the source…' : 'Review entities'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const ARTIFACT_KIND_LABEL = {
  filing: 'filing / certificate', exhibit: 'exhibit', prior: 'prior version',
  email: 'email thread', other: 'other',
};

// ---- Document identity: emojis + paper thumbnails --------------------------------
// A document's role in the project should read at a glance, before any text:
// ⭐ the starting point, 🌱 the original that seeded the project, 🧬 an
// AI-derived draft. Multiple glyphs stack — a doc can be the starting point
// AND derived. Tooltips carry the words.
function docEmojis(d, deal) {
  const tags = (d.tags || []).map((t) => String(t).toLowerCase());
  const out = [];
  if (deal?.coreDocId === d.id) out.push({ e: '⭐', tip: 'Starting point — the project’s core document' });
  if (tags.includes('original') || tags.includes('source-template')) out.push({ e: '🌱', tip: 'Original / source template — the document this project grew from' });
  if (tags.includes('derived') || d.format === 'derived') out.push({ e: '🧬', tip: 'AI-derived from the project’s original' });
  if (tags.includes('executed')) out.push({ e: '✍️', tip: 'Executed / signed' });
  if (tags.includes('superseded')) out.push({ e: '🗄️', tip: 'Superseded — kept for history' });
  if (tags.includes('received')) out.push({ e: '📥', tip: 'Received from the counterparty' });
  if (d.kind === 'filing') out.push({ e: '🏛️', tip: 'Filing / certificate — evidence on record' });
  if (d.kind === 'email') out.push({ e: '✉️', tip: 'Email thread' });
  if (d.kind === 'exhibit') out.push({ e: '📎', tip: 'Exhibit / schedule' });
  if (d.kind === 'prior') out.push({ e: '🗂️', tip: 'Prior version' });
  if (docViewOf(d) === 'deck') out.push({ e: '📊', tip: 'Presentation' });
  else if (docViewOf(d) === 'design') out.push({ e: '📐', tip: 'Design document' });
  else if (d.kind === 'primary') out.push({ e: '📜', tip: 'Agreement' });
  return out;
}

function EmojiCluster({ doc, deal, max = 4, size = 15 }) {
  const list = docEmojis(doc, deal).slice(0, max);
  if (!list.length) return null;
  return (
    <span style={{ fontSize: size, lineHeight: 1, flexShrink: 0, letterSpacing: 1 }} aria-hidden="true">
      {list.map((x) => <span key={x.e} data-tip={x.tip}>{x.e}</span>)}
    </span>
  );
}

// File-type identity — a thumbnail should read as the artifact it is. Decks
// render as a landscape slide; everything else is a paper page carrying the
// classic colored format tab (DOCX blue, PDF red, …). AI-authored/derived
// drafts are app-native (they export to any format), so they carry "AI".
const FILE_TYPE_STYLE = {
  PPTX: '#C43E1C', DOCX: '#2B579A', PDF: '#B3261E', TXT: '#5F6B72', EML: '#8C6D1F', AI: '#6C4AB6', XLSX: '#1D6F42',
};
function docFileType(d) {
  if (docViewOf(d) === 'deck') return 'PPTX';
  if (docViewOf(d) === 'workbook') return 'XLSX';
  const f = String(d.format || '').toLowerCase();
  if (f === 'xlsx' || f === 'xlsm') return 'XLSX';
  if (f === 'docx' || f === 'doc') return 'DOCX';
  if (f === 'pdf') return 'PDF';
  if (f === 'txt') return 'TXT';
  if (f === 'pasted') return d.kind === 'email' ? 'EML' : 'TXT';
  if (f === 'authored' || f === 'derived') return 'AI';
  return null;
}

function FileTypeTab({ type, small = false }) {
  if (!type) return null;
  return (
    <span
      style={{
        position: 'absolute', top: small ? 3 : 4, right: small ? 3 : 4,
        background: FILE_TYPE_STYLE[type] || '#5F6B72', color: '#fff',
        fontSize: small ? 5.5 : 6.5, fontWeight: 700, letterSpacing: '0.04em',
        padding: '1px 3px', borderRadius: 2, lineHeight: 1.4,
        fontFamily: 'var(--font-sans, sans-serif)',
      }}
      aria-hidden="true"
    >
      {type}
    </span>
  );
}

// Miniature of the document itself, linking into it: decks are a dark
// landscape slide (the deck theme), papers are the white-page identity from
// the document view — both wear the format tab + the doc's emoji badges.
function DocThumb({ doc, deal, to, size = 64 }) {
  const emojis = docEmojis(doc, deal).slice(0, 3);
  const core = deal?.coreDocId === doc.id;
  const fileType = docFileType(doc);
  const summary = doc.aiProfile?.summary || '';

  const badge = emojis.length > 0 && (
    <span
      style={{
        position: 'absolute', bottom: -7, right: -7, display: 'flex', gap: 0,
        fontSize: 14, lineHeight: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
      }}
      aria-hidden="true"
    >
      {emojis.map((x) => <span key={x.e} title={x.tip}>{x.e}</span>)}
    </span>
  );

  if (docViewOf(doc) === 'deck') {
    // 16:9 slide in the deck export's palette: teal kicker rule, sans title,
    // a faux bullet block — unmistakably a presentation next to the papers.
    const w = Math.round(size * 1.32);
    const h = Math.round(w * 9 / 16);
    return (
      <Link to={to} title={doc.title} style={{ position: 'relative', flexShrink: 0, textDecoration: 'none', display: 'block' }}>
        <div
          style={{
            width: w, height: h, background: '#0D1517', borderRadius: 3, padding: '6px 7px',
            overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.45)',
            border: core ? '2px solid var(--info)' : '1px solid rgba(255,255,255,0.18)',
          }}
        >
          <div style={{ width: 12, height: 2, background: '#00B5AC', borderRadius: 1, marginBottom: 3 }} />
          <div
            style={{
              fontFamily: 'var(--font-sans, sans-serif)', fontSize: 6, fontWeight: 700,
              color: '#EAF4F5', lineHeight: 1.3, maxHeight: 16, overflow: 'hidden', marginBottom: 4,
            }}
          >
            {doc.title}
          </div>
          {[70, 58, 64].map((lw, i) => (
            <div key={i} style={{ height: 2, background: '#24393F', borderRadius: 1, marginBottom: 3, width: `${lw}%` }} />
          ))}
        </div>
        <FileTypeTab type={fileType} small />
        {badge}
      </Link>
    );
  }

  const h = Math.round(size * 1.32);
  const widths = [100, 94, 98, 86, 97, 62];
  return (
    <Link
      to={to}
      title={doc.title}
      style={{ position: 'relative', flexShrink: 0, textDecoration: 'none', display: 'block' }}
    >
      <div
        style={{
          width: size, height: h, background: '#fff', borderRadius: 3, padding: '7px 6px',
          overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.45)',
          border: core ? '2px solid var(--info)' : '1px solid rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            fontFamily: "'Times New Roman', Georgia, serif", fontSize: 5.5, fontWeight: 700,
            color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3, textTransform: 'uppercase',
            marginBottom: 4, maxHeight: 22, overflow: 'hidden',
            paddingRight: fileType ? 10 : 0,
          }}
        >
          {doc.title}
        </div>
        {widths.map((w, i) => (
          <div key={i} style={{ height: 2, background: '#cfcfcf', borderRadius: 1, marginBottom: 3, width: `${w}%` }} />
        ))}
        {summary && (
          <div style={{ fontFamily: "'Times New Roman', Georgia, serif", fontSize: 4.5, color: '#8a8a8a', lineHeight: 1.4, textAlign: 'justify' }}>
            {summary.slice(0, 120)}
          </div>
        )}
      </div>
      <FileTypeTab type={fileType} />
      {badge}
    </Link>
  );
}

function EditDocTagsModal({ scope, doc, participant, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [tags, setTags] = useState(doc.tags || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await updateEntity('dealDoc', scope, doc.id, { tags });
      await appendAudit(scope, actorOf(user, participant), 'doc.tagged', `"${doc.title}" tagged: ${tags.map((t) => `#${t}`).join(' ') || '(none)'}`, { docId: doc.id });
      toast('Tags saved — the brain reindexes on the next ask', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };
  return (
    <Modal title={`Tags — ${doc.title}`} onClose={onClose} maxWidth={620}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <span className="u-small u-muted">
          Provenance tags matter most: mark the document this project started from as{' '}
          <strong>original</strong> / <strong>source-template</strong>, drafts produced from it as{' '}
          <strong>derived</strong>. Tags index into the project brain.
        </span>
        <DocTagPicker value={tags} onChange={setTags} />
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save tags'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Folder mover — light project organization: file a document under a folder
// label ('Internal' work product vs 'External' — sent to / received from the
// counterparty — or any custom label). The Documents tab groups and filters
// by folder; unfiled docs stay in the main flow.
const FOLDER_PRESETS = ['Internal', 'External'];
function EditDocFolderModal({ scope, doc, folders, participant, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const options = [...new Set([...FOLDER_PRESETS, ...(folders || [])])];
  const [folder, setFolder] = useState(doc.folder || '');
  const [custom, setCustom] = useState(options.includes(doc.folder) || !doc.folder ? '' : doc.folder);
  const [busy, setBusy] = useState(false);
  const chosen = (custom.trim() || folder).slice(0, 60);
  const save = async () => {
    setBusy(true);
    try {
      await updateEntity('dealDoc', scope, doc.id, { folder: chosen });
      await appendAudit(scope, actorOf(user, participant), 'doc.foldered',
        chosen ? `"${doc.title}" filed under 📁 ${chosen}` : `"${doc.title}" removed from its folder`, { docId: doc.id });
      toast(chosen ? `Filed under ${chosen}` : 'Removed from folder', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };
  return (
    <Modal title={`Folder — ${doc.title}`} onClose={onClose} maxWidth={520}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <span className="u-small u-muted">
          File this document under a folder. <strong>Internal</strong> = work product for your side only
          (memos, positions, working redlines); <strong>External</strong> = sent to or received from the
          counterparty. Unfiled documents stay in the main list.
        </span>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          <button
            type="button"
            className="cs-chip"
            style={!chosen ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
            onClick={() => { setFolder(''); setCustom(''); }}
          >
            No folder
          </button>
          {options.map((f) => (
            <button
              key={f}
              type="button"
              className="cs-chip"
              style={chosen === f && !custom.trim() ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              onClick={() => { setFolder(f); setCustom(''); }}
            >
              📁 {f}
            </button>
          ))}
        </div>
        <input
          className="cs-input"
          placeholder="…or a custom folder name"
          maxLength={60}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="cs-btn cs-btn-primary" disabled={busy || (doc.folder || '') === chosen} onClick={save}>
            {busy ? 'Saving…' : chosen ? `File under ${chosen}` : 'Remove from folder'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Per-agreement role — what THIS agreement must be within the project's
// ecosystem. Anchors its purpose audits and travels in the ecosystem brief
// every AI pass on sibling agreements sees.
function EditDocRoleModal({ scope, doc, participant, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [text, setText] = useState(doc.objective || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await updateEntity('dealDoc', scope, doc.id, { objective: text.trim() });
      await appendAudit(scope, actorOf(user, participant), 'deal.objective',
        `Agreement role ${doc.objective ? 'updated' : 'set'} on "${doc.title}": ${text.trim().slice(0, 140)}`, { docId: doc.id });
      toast('Role saved — audits and AI edits on this agreement anchor to it', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };
  return (
    <Modal title={`Role — ${doc.title}`} onClose={onClose} maxWidth={620}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <span className="u-small u-muted">
          One or two sentences on what this agreement must be — its layer in the structure, its parties, and
          what does NOT belong in it. The purpose audit benchmarks against this; every AI pass on the other
          agreements sees it as ecosystem context.
        </span>
        <textarea
          className="cs-input"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'E.g. "Services & Equity Award Agreement between HoldCo, OpCo, and the consultant — owns all consultant equity mechanics; the operating agreements only cross-reference it."'}
        />
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// One download for the whole deliverable set ("open items + changes memo +
// redline + clean copy"): checklist of ready documents, per-doc clean/redline
// picks, a format toggle, and the optional negotiation-points PDF — zipped
// server-side by the exact builders behind the single-export buttons.
function ExportPackageModal({ scope, deal, docs, participant, onClose }) {
  const toast = useToast();
  const ready = (docs || []).filter((d) => d.status === 'ready' && d.currentVersionId && d.format !== 'pasted');
  const [picks, setPicks] = useState(() => {
    // Sensible defaults: working (non-superseded) primary documents export
    // clean + redline (redline only when a base version exists to diff).
    const init = {};
    for (const d of ready) {
      const working = d.kind === 'primary' && !isSupersededDoc(d);
      init[d.id] = {
        clean: working,
        redline: working && (d.versionCount || 0) > 1 && docViewOf(d) !== 'deck' && docViewOf(d) !== 'workbook',
      };
    }
    return init;
  });
  const [format, setFormat] = useState('docx'); // 'docx' | 'pdf' | 'both'
  const [structure, setStructure] = useState('auto'); // 'auto' | 'flat' | 'mode' | 'doc'
  const [includePoints, setIncludePoints] = useState(false);
  const [letterhead, setLetterhead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const negotiated = isNegotiated(deal);

  const toggle = (id, key) => setPicks((p) => ({ ...p, [id]: { ...p[id], [key]: !p[id]?.[key] } }));

  const items = [];
  for (const d of ready) {
    const p = picks[d.id] || {};
    // Decks and workbooks have one native format (.pptx / .xlsx) — request
    // once; the server routes to the right builder regardless of format.
    const native = docViewOf(d) === 'deck' || docViewOf(d) === 'workbook';
    const formats = native ? ['docx'] : format === 'both' ? ['docx', 'pdf'] : [format];
    for (const f of formats) {
      // Letterhead applies to clean Word copies only — redlines and PDFs
      // keep their normal fidelity paths.
      if (p.clean) items.push({ docId: d.id, mode: 'clean', format: f, ...(letterhead && f === 'docx' && !native ? { letterhead: true } : {}) });
      if (p.redline && !native) items.push({ docId: d.id, mode: 'redline', format: f });
    }
  }
  const count = items.length + (includePoints ? 1 : 0);
  const overCap = items.length > 16;

  const params = () => ({
    orgId: scope.org,
    dealId: scope.deal,
    items: items.slice(0, 16),
    includeDealPoints: includePoints,
    oursPartyKey: participant?.partyKey || '',
    structure,
  });

  const run = async () => {
    setBusy(true);
    try {
      await downloadPackageExport(params());
      toast(`Package downloaded — ${count} artifact${count === 1 ? '' : 's'}`, 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setShareBusy(true);
    try {
      const r = await createPackageShareLink(params());
      setShareLink(r);
      try { await navigator.clipboard.writeText(r.url); toast('Share link copied to clipboard', 'ok'); } catch { /* shown below */ }
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <Modal title="Export package" onClose={onClose} maxWidth={680}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <p className="u-small u-muted" style={{ margin: 0 }}>
          Pick the artifacts and download them as one zip — each file is identical to its single-export
          button (clean Word/PDF, genuine tracked-changes redlines with authorship, annotated redline PDFs).
        </p>
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)', maxHeight: 320, overflow: 'auto' }}>
          {ready.map((d) => {
            const p = picks[d.id] || {};
            const native = docViewOf(d) === 'deck' || docViewOf(d) === 'workbook';
            const canRedline = !native && (d.versionCount || 0) > 1;
            return (
              <div key={d.id} className="u-split" style={{ alignItems: 'center', gap: 'var(--sp-3)' }}>
                <span className="u-small" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <strong>{d.title}</strong>{' '}
                  <span className="u-xs u-muted">v{d.versionCount || 0}{native ? ` · ${docViewOf(d) === 'deck' ? '.pptx' : '.xlsx'}` : ''}</span>
                </span>
                <span className="u-cluster" style={{ '--gap': 'var(--sp-3)', flexShrink: 0 }}>
                  <label className="u-small u-cluster" style={{ '--gap': 'var(--sp-1)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!p.clean} onChange={() => toggle(d.id, 'clean')} /> Clean
                  </label>
                  <label
                    className="u-small u-cluster"
                    style={{ '--gap': 'var(--sp-1)', cursor: canRedline ? 'pointer' : 'not-allowed', opacity: canRedline ? 1 : 0.45 }}
                    title={canRedline ? undefined : native ? 'Decks and workbooks export in their native format only' : 'Needs a base version to diff against (this is v1)'}
                  >
                    <input type="checkbox" disabled={!canRedline} checked={!!p.redline} onChange={() => toggle(d.id, 'redline')} /> Redline
                  </label>
                </span>
              </div>
            );
          })}
          {!ready.length && <div className="cs-empty">No exportable documents yet.</div>}
        </div>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-4)', alignItems: 'center' }}>
          <label className="u-small u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
            Format
            <select className="cs-input cs-select-inline" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="docx">Word</option>
              <option value="pdf">PDF</option>
              <option value="both">Word + PDF</option>
            </select>
          </label>
          <label className="u-small u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }} title="How files are arranged inside the zip">
            Folders
            <select className="cs-input cs-select-inline" value={structure} onChange={(e) => setStructure(e.target.value)}>
              <option value="auto">Auto</option>
              <option value="flat">None (flat)</option>
              <option value="mode">Clean / Redlines</option>
              <option value="doc">By document</option>
            </select>
          </label>
          {negotiated && (
            <label className="u-small u-cluster" style={{ '--gap': 'var(--sp-1)', cursor: 'pointer' }}>
              <input type="checkbox" checked={includePoints} onChange={() => setIncludePoints((v) => !v)} />
              Negotiation points PDF (stance by party)
            </label>
          )}
          {(format === 'docx' || format === 'both') && (
            <label
              className="u-small u-cluster"
              style={{ '--gap': 'var(--sp-1)', cursor: 'pointer' }}
              title="Clean Word copies render on the organization's letterhead theme (set under Team → Letterhead)"
            >
              <input type="checkbox" checked={letterhead} onChange={() => setLetterhead((v) => !v)} />
              Letterhead clean copies
            </label>
          )}
        </div>
        {overCap && (
          <div className="cs-banner b-warn u-small">A package caps at 16 artifacts — the first 16 will be included. Narrow the picks or switch off Word + PDF.</div>
        )}
        {shareLink && (
          <div className="cs-banner b-ok u-small u-stack" style={{ '--gap': 'var(--sp-1)' }}>
            <strong>Share link created — expires in {shareLink.expiresDays} days.</strong>
            <span style={{ wordBreak: 'break-all' }}>{shareLink.url}</span>
            <span className="u-muted">Recipients download the zip without an account. The package is frozen as of now — new versions need a new link.</span>
          </div>
        )}
        <div className="u-split">
          <span className="u-small u-muted">{count} artifact{count === 1 ? '' : 's'} selected</span>
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            <button
              className="cs-btn cs-btn-outline"
              disabled={shareBusy || !count}
              data-tip="Build the package once, get an expiring link anyone can download — no account needed"
              onClick={share}
            >
              {shareBusy ? 'Creating link…' : 'Copy share link'}
            </button>
            <button className="cs-btn cs-btn-primary" disabled={busy || !count} onClick={run}>
              {busy ? 'Building package…' : 'Download zip'}
            </button>
          </span>
        </div>
      </div>
    </Modal>
  );
}

// Draft a send-ready email from the live deal state (documents, versions,
// register). The counterparty audience is stripped server-side to circulated
// facts only — no internal stances can leak into the draft. Nothing sends:
// the deliverable is subject + body to copy into your mail client.
function DraftEmailModal({ scope, onClose }) {
  const toast = useToast();
  const [audience, setAudience] = useState('internal');
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      setDraft(await aiDraftEmail({ orgId: scope.org, dealId: scope.deal, audience, focus }));
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
      toast('Copied to clipboard', 'ok');
    } catch {
      toast('Copy failed — select the text manually', 'danger');
    }
  };

  return (
    <Modal title="Draft email" onClose={onClose} maxWidth={720}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'flex-end' }}>
          <div className="cs-field">
            <label htmlFor="de-aud">Audience</label>
            <select id="de-aud" className="cs-input" value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="internal">Internal team</option>
              <option value="counsel">Outside counsel</option>
              <option value="counterparty">Counterparty</option>
            </select>
          </div>
          <div className="cs-field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="de-focus">Focus (optional)</label>
            <input
              id="de-focus"
              className="cs-input"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="e.g. the v16 changes and what we need decided by Friday"
            />
          </div>
          <button className="cs-btn cs-btn-primary" disabled={busy} onClick={run}>
            {busy ? 'Drafting…' : draft ? 'Redraft' : 'Draft'}
          </button>
        </div>
        {audience === 'counterparty' && (
          <div className="cs-banner b-warn u-small">
            Counterparty drafts use only circulated facts — internal stances and unsent asks are withheld. Still
            review before sending.
          </div>
        )}
        {draft && (
          <>
            <div className="cs-field">
              <label>Subject</label>
              <input className="cs-input" readOnly value={draft.subject} />
            </div>
            <div className="cs-field">
              <label>Body</label>
              <textarea className="cs-input" rows={14} readOnly value={draft.body} />
            </div>
            <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
              <button className="cs-btn cs-btn-outline" onClick={copy}>Copy subject + body</button>
              <button className="cs-btn cs-btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Pending drafting-agent proposals on a document — surfaced on the project's
// document list so reviews don't rot unseen inside the doc page (data lives
// in the doc's `proposals` subcollection; working docs are few, so one
// listener per row is cheap).
function PendingProposalBadge({ scope, doc }) {
  const { docs: proposals } = useEntityList('proposal', { ...scope, dealDoc: doc.id }, { enabled: doc.status === 'ready' });
  const pending = (proposals || []).filter((p) => p.status === 'pending').length;
  if (!pending) return null;
  return (
    <span className="cs-badge b-warn" data-tip="Drafting-agent proposals awaiting review — open the document to apply, revise, or dismiss them">
      ✍ {pending} proposal{pending === 1 ? '' : 's'} awaiting review
    </span>
  );
}

// File cabinets — every project's paper flows IN (data exports, invoices,
// hours files, received drafts), gets WORKED (drafts, memos, analysis — the
// main list below), and goes OUT (deliverables: the files actually sent).
// `dealDoc.cabinet` ('input' | 'deliverable', absent = working paper) is the
// field; legacy `#input` / `#deliverable` tags are honored until backfilled.
// Deliverables download as exact bytes: the reference files pinned on the
// current checkpoint (Excel books built outside the model, signed PDFs,
// prints) or, failing those, the original upload — never the model export,
// which for workbooks is a styling-free skeleton.
const CABINETS = {
  input: {
    label: 'Inputs', icon: '📥',
    hint: 'What came in — data exports, invoices, hours files, received drafts',
    empty: 'Nothing filed as an input yet — choose Input when adding a document, or Cabinet… on one already here.',
  },
  deliverable: {
    label: 'Deliverables', icon: '📤',
    hint: 'What goes out — the files to send, exactly as built',
    empty: 'Nothing filed as a deliverable yet — choose Deliverable when adding a document, or Cabinet… on one already here.',
  },
};
function CabinetRow({ scope, doc, cabinet, canDownload, toast, onMove }) {
  const { doc: version } = useDoc(
    doc.currentVersionId ? `orgs/${scope.org}/deals/${scope.deal}/docs/${doc.id}/versions/${doc.currentVersionId}` : ''
  );
  const refs = (version?.files || []).filter((f) => f.role === 'reference' && f.s3Key);
  const original = doc.s3Key ? [{ name: `${doc.title}${(doc.s3Key.match(/\.\w+$/) || [''])[0]}`, s3Key: doc.s3Key }] : [];
  // Deliverables prefer the pinned exact-bytes files; inputs ARE the upload.
  const targets = cabinet === 'deliverable' && refs.length ? refs : original;
  const grab = (f) => downloadAsset({ key: f.s3Key, name: f.name }).catch((err) => toast(err.message, 'danger'));
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)', minWidth: 0 }}>
      <span className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
        <Link to={`/deals/${scope.deal}/docs/${doc.id}`} className="u-small u-truncate" style={{ textDecoration: 'none' }}>
          {doc.title}
        </Link>
        <span className="u-xs u-muted u-tabular">
          {docFileType(doc) || doc.format || '—'}
          {version ? ` · v${version.number}` : ''}
          {' · '}
          {cabinet === 'deliverable' && refs.length ? 'exact bytes pinned to this checkpoint' : doc.s3Key ? 'original upload' : 'pasted / authored — no file'}
          {' · '}added {compactWhen(doc.createdAt)}
        </span>
      </span>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center', minWidth: 0 }}>
        {canDownload && targets.map((f) => (
          <button
            key={f.s3Key}
            type="button"
            className={`cs-btn cs-btn-sm u-truncate ${cabinet === 'deliverable' ? 'cs-btn-primary' : 'cs-btn-outline'}`}
            style={{ maxWidth: '100%', display: 'block' }}
            data-tip={`Download ${f.name}${f.sha256 ? ` · SHA-256 ${f.sha256.slice(0, 16)}…` : ''}`}
            onClick={() => grab(f)}
          >
            ⬇ {f.name}
          </button>
        ))}
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" title="Move to another cabinet" onClick={() => onMove(doc)}>
          Cabinet…
        </button>
      </span>
    </div>
  );
}

function FileCabinets({ scope, docs, canDownload, toast, onMove }) {
  const byAge = (a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0);
  return (
    <div className="u-grid" style={{ '--min': '320px', '--gap': 'var(--sp-3)' }}>
      {['input', 'deliverable'].map((key) => {
        const c = CABINETS[key];
        const list = (docs || []).filter((d) => cabinetOf(d) === key).sort(byAge);
        return (
          <section
            key={key}
            className="cs-card u-stack"
            style={{ padding: 'var(--sp-4)', '--gap': 'var(--sp-3)', ...(key === 'deliverable' && list.length ? { borderColor: 'var(--border-strong)' } : {}) }}
            aria-label={`${c.label} cabinet`}
          >
            <span className="u-stack" style={{ '--gap': '2px' }}>
              <strong>{c.icon} {c.label} ({list.length})</strong>
              <span className="u-xs u-muted">{c.hint}</span>
            </span>
            {list.length
              ? list.map((d) => <CabinetRow key={d.id} scope={scope} doc={d} cabinet={key} canDownload={canDownload} toast={toast} onMove={onMove} />)
              : <span className="u-xs u-muted">{c.empty}</span>}
          </section>
        );
      })}
    </div>
  );
}

function EditDocCabinetModal({ scope, doc, participant, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [cabinet, setCabinet] = useState(cabinetOf(doc));
  const [busy, setBusy] = useState(false);
  const LABEL = { '': 'Working paper', input: 'Inputs', deliverable: 'Deliverables' };
  const save = async () => {
    setBusy(true);
    try {
      // Legacy tag-derived placement is made explicit on save so the tag no
      // longer decides; the tags themselves are left alone.
      await updateEntity('dealDoc', scope, doc.id, { cabinet });
      await appendAudit(scope, actorOf(user, participant), 'doc.cabineted',
        `"${doc.title}" filed in ${LABEL[cabinet]}`, { docId: doc.id, cabinet });
      toast(`Filed in ${LABEL[cabinet]}`, 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
      setBusy(false);
    }
  };
  const Option = ({ value, title, body }) => (
    <button
      type="button"
      className="cs-card u-stack"
      style={{ padding: 'var(--sp-3) var(--sp-4)', '--gap': '2px', textAlign: 'left', cursor: 'pointer', borderColor: cabinet === value ? 'var(--surf)' : undefined }}
      aria-pressed={cabinet === value}
      onClick={() => setCabinet(value)}
    >
      <strong className="u-small">{title}</strong>
      <span className="u-xs u-muted">{body}</span>
    </button>
  );
  return (
    <Modal title={`Cabinet — ${doc.title}`} onClose={onClose} maxWidth={520}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <Option value="input" title="📥 Inputs" body="What came in — data exports, invoices, hours files, drafts received from the other side." />
        <Option value="" title="📝 Working paper" body="Drafts, memos, analysis, positions — the documents being worked in this project." />
        <Option value="deliverable" title="📤 Deliverables" body="What goes out — the files actually sent, downloadable exactly as built." />
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="cs-btn cs-btn-primary" disabled={busy || cabinetOf(doc) === cabinet} onClick={save}>
            {busy ? 'Saving…' : `File in ${LABEL[cabinet]}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DocumentsTab({ scope, deal, participant }) {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useDealPowers(scope.deal);
  const { docs } = useEntityList('dealDoc', scope);
  const [showUpload, setShowUpload] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [draftKind, setDraftKind] = useState('document');
  const [kindFilter, setKindFilter] = useState('');
  const [samBusy, setSamBusy] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // SAM pursuit projects (spawned from the /sam desk, tagged 'sam') get the
  // two pursuit actions: compliance-matrix extraction and response drafting.
  const isSamPursuit = (deal?.tags || []).includes('sam');
  const runSamCompliance = async () => {
    setSamBusy('compliance');
    try {
      const r = await samCompliance({ orgId: scope.org, dealId: scope.deal });
      toast(`Compliance matrix extracted from "${r.docTitle}" — ${r.created.length} suggested requirement${r.created.length === 1 ? '' : 's'} filed on the register`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally { setSamBusy(''); }
  };
  const runSamDraft = async () => {
    setSamBusy('draft');
    try {
      const r = await samDraftResponse({ orgId: scope.org, dealId: scope.deal, kind: 'rfi' });
      toast(`"${r.title}" drafted — review every [CONFIRM] placeholder before submission`, 'ok');
      if (r.docId) navigate(`/deals/${scope.deal}/docs/${r.docId}`);
    } catch (err) {
      toast(err.message, 'danger');
    } finally { setSamBusy(''); }
  };

  // Deep links from the sidebar's Create menu: ?draft=<document|deck|workbook>
  // opens Draft with AI preset to the kind; ?add=1 opens the add-document
  // portal. Params strip after opening so refresh/back don't re-trigger.
  const draftParam = searchParams.get('draft');
  const addParam = searchParams.get('add');
  useEffect(() => {
    if (!draftParam && addParam !== '1') return;
    if (draftParam && isAiConfigured() && can('agent')) {
      setDraftKind(['document', 'deck', 'workbook'].includes(draftParam) ? draftParam : 'document');
      setShowDraft(true);
    }
    if (addParam === '1' && can('upload')) setShowUpload(true);
    const next = new URLSearchParams(searchParams);
    next.delete('draft');
    next.delete('add');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftParam, addParam]);
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [showPackage, setShowPackage] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [refactorDoc, setRefactorDoc] = useState(null);
  const [tagsDoc, setTagsDoc] = useState(null);
  const [roleDoc, setRoleDoc] = useState(null);
  const [folderDoc, setFolderDoc] = useState(null);
  const [cabinetDoc, setCabinetDoc] = useState(null);
  const [folderFilter, setFolderFilter] = useState('');

  // One deal usually means ONE working document (with many versions) plus a
  // trail of artifacts: the original source files it was seeded from,
  // reference prints, filed correspondence. Never present those as peers.
  // The list orders by last activity so "which is latest" reads top-down;
  // the freshest doc wears a "latest" badge and the starting point keeps its
  // ⭐ badge + border wherever it lands. Superseded drafts are lineage, not
  // peers — they fold into their own section below.
  const touchedAt = (d) => d.updatedAt || d.createdAt || 0;
  // Cabinet docs (inputs / deliverables) live in their cabinets up top and
  // leave the working flow — one home per document.
  const uncabineted = (docs || []).filter((d) => !cabinetOf(d));
  const primaries = uncabineted.filter((d) => d.kind === 'primary');
  const working = primaries
    .filter((d) => !isSupersededDoc(d))
    .sort((a, b) => touchedAt(b) - touchedAt(a));
  const latestId = working.length > 1 ? working[0].id : null;

  // Kind chips — when a project mixes documents, presentations, and
  // workbooks, filter the working list by artifact kind (mirrors the
  // library's buckets; chips only render when there is a mix).
  const workKindOf = (d) => (docViewOf(d) === 'deck' ? 'deck' : docViewOf(d) === 'workbook' ? 'workbook' : 'document');
  const WORK_KIND_LABEL = { document: 'Documents', deck: 'Presentations', workbook: 'Workbooks' };
  const kindsPresent = ['document', 'deck', 'workbook'].filter((k) => working.some((d) => workKindOf(d) === k));

  // Folder chips — projects can file documents under folder labels
  // ('Internal' work product vs 'External' — sent to / received from the
  // counterparty). Chips render only when at least one doc is filed; the
  // filter applies to every section (working, superseded, artifacts).
  const foldersPresent = [...new Set((docs || []).map((d) => d.folder).filter(Boolean))].sort();
  const inFolder = (d) => !folderFilter || (folderFilter === '__unfiled' ? !d.folder : d.folder === folderFilter);
  const folderCount = (f) => (docs || []).filter((d) => d.folder === f).length;
  const unfiledCount = (docs || []).filter((d) => !d.folder).length;

  const shownWorking = (kindFilter ? working.filter((d) => workKindOf(d) === kindFilter) : working).filter(inFolder);
  const supersededDocs = primaries.filter(isSupersededDoc).filter(inFolder);
  const titleOf = (id) => (docs || []).find((x) => x.id === id)?.title;
  const artifacts = uncabineted.filter((d) => d.kind !== 'primary').filter(inFolder);

  const markCore = async (d) => {
    try {
      await updateEntity('deal', { org: scope.org }, scope.deal, { coreDocId: d.id });
      await appendAudit(scope, actorOf(user, participant), 'doc.coreMarked', `"${d.title}" marked as the project's starting point`, { docId: d.id });
      toast(`"${d.title}" is now the starting point`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split">
        <span className="u-muted u-small">Word, PDF, Excel, text, pasted email threads — or a whole folder or .zip — imported into versioned clauses.</span>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {isAiConfigured() && can('agent') && (
            <button className="cs-btn cs-btn-outline" onClick={() => setShowDraft(true)}>Draft with AI</button>
          )}
          {isSamPursuit && isAiConfigured() && can('agent') && (
            <>
              <button
                className="cs-btn cs-btn-outline"
                data-tip="Read the ingested solicitation and file its requirements — submission rules, eligibility, technical shalls, evaluation criteria — as suggested points on the register"
                disabled={samBusy === 'compliance'}
                onClick={runSamCompliance}
              >
                {samBusy === 'compliance' ? 'Extracting…' : 'Compliance matrix'}
              </button>
              <button
                className="cs-btn cs-btn-outline"
                data-tip="Draft the RFI / Sources Sought response from the notice, attachments, and standing capability facts — unconfirmed claims become [CONFIRM] placeholders"
                disabled={samBusy === 'draft'}
                onClick={runSamDraft}
              >
                {samBusy === 'draft' ? 'Drafting…' : 'Draft SAM response'}
              </button>
            </>
          )}
          {can('edit') && (docs || []).some((d) => d.status === 'ready' && d.currentVersionId) && (
            <button className="cs-btn cs-btn-outline" onClick={() => setShowEnvelope(true)}>Request signatures</button>
          )}
          {can('edit') && (
            <button className="cs-btn cs-btn-outline" onClick={() => setShowCertificate(true)}>Issue certificate</button>
          )}
          {can('download') && (docs || []).some((d) => d.status === 'ready' && d.currentVersionId) && (
            <button
              className="cs-btn cs-btn-outline"
              data-tip="Clean copies, redlines, and the negotiation-points PDF as one zip — the full response package in a single download"
              onClick={() => setShowPackage(true)}
            >
              Export package
            </button>
          )}
          {isAiConfigured() && (
            <button
              className="cs-btn cs-btn-outline"
              data-tip="Draft a send-ready email from the live deal state — internal update, counsel briefing, or counterparty note"
              onClick={() => setShowEmail(true)}
            >
              Draft email
            </button>
          )}
          {can('upload') && (
            <button className="cs-btn cs-btn-outline" onClick={() => setShowUpload(true)}>Add document</button>
          )}
        </span>
      </div>
      {docs === null && <div className="cs-skeleton" style={{ height: 120 }} />}
      {docs && !docs.length && (
        <div className="cs-empty">
          Nothing here yet — this is where the project's paper lives. Start by uploading the core
          agreement or draft (DOCX or PDF, both together if you have them), or describe a new document
          and let the drafting agent author v1. Everything you add is version-tracked and embedded into
          the <Term term="project brain">project brain</Term>. <HelpDot topic="uploads" label="How uploads work" />
        </div>
      )}
      {docs && docs.length > 0 && (
        <FileCabinets scope={scope} docs={docs} canDownload={can('download')} toast={toast} onMove={setCabinetDoc} />
      )}
      {(shownWorking.length > 0 || supersededDocs.length > 0 || artifacts.length > 0) && (
        <span className="u-small u-muted" style={{ marginTop: 'var(--sp-2)' }}>
          📝 Working paper — drafts, memos, analysis. Use <strong>Cabinet…</strong> to file a document as an input or a deliverable.
        </span>
      )}
      {kindsPresent.length > 1 && (
        <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
          <button
            type="button"
            className="cs-chip"
            style={kindFilter === '' ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
            onClick={() => setKindFilter('')}
          >
            All ({working.length})
          </button>
          {kindsPresent.map((k) => (
            <button
              key={k}
              type="button"
              className="cs-chip"
              style={kindFilter === k ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              onClick={() => setKindFilter(kindFilter === k ? '' : k)}
            >
              {WORK_KIND_LABEL[k]} ({working.filter((d) => workKindOf(d) === k).length})
            </button>
          ))}
        </div>
      )}
      {foldersPresent.length > 0 && (
        <div className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)' }}>
          <button
            type="button"
            className="cs-chip"
            style={folderFilter === '' ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
            onClick={() => setFolderFilter('')}
          >
            All folders
          </button>
          {foldersPresent.map((f) => (
            <button
              key={f}
              type="button"
              className="cs-chip"
              style={folderFilter === f ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              onClick={() => setFolderFilter(folderFilter === f ? '' : f)}
            >
              📁 {f} ({folderCount(f)})
            </button>
          ))}
          {unfiledCount > 0 && (
            <button
              type="button"
              className="cs-chip"
              style={folderFilter === '__unfiled' ? { borderColor: 'var(--surf)' } : { cursor: 'pointer' }}
              onClick={() => setFolderFilter(folderFilter === '__unfiled' ? '' : '__unfiled')}
            >
              Unfiled ({unfiledCount})
            </button>
          )}
        </div>
      )}
      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {shownWorking.map((d) => (
          <div
            key={d.id}
            className="cs-card u-stack"
            style={{ '--gap': 'var(--sp-3)', ...(d.id === deal.coreDocId ? { borderLeft: '3px solid var(--info)' } : {}) }}
          >
            <div className="u-cluster" style={{ '--gap': 'var(--sp-4)', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <DocThumb doc={d} deal={deal} to={`/deals/${scope.deal}/docs/${d.id}`} />
            <div className="u-split" style={{ flex: 1, minWidth: 0 }}>
            <div className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                <Link to={`/deals/${scope.deal}/docs/${d.id}`} style={{ textDecoration: 'none' }}>
                  <strong>{d.title}</strong>
                </Link>
                {d.id === latestId && (
                  <span className="cs-badge b-ok" data-tip="Most recently updated document in this project — the freshest paper is always on top">
                    ● latest
                  </span>
                )}
                {d.id === deal.coreDocId && (
                  <span className="cs-badge b-info" data-tip="The project's core document — everything else relates back to it">
                    ★ starting point
                  </span>
                )}
                {(d.tags || []).includes('executed') && (
                  <span className="cs-badge b-ok" data-tip="Signed & executed — authoritative context: AI passes on the other agreements treat its concepts as settled here">
                    ⚖️ signed
                  </span>
                )}
                {d.folder && <span className="cs-chip u-xs">📁 {d.folder}</span>}
                <PendingProposalBadge scope={scope} doc={d} />
              </span>
              <span className="u-small u-muted u-tabular">
                v{d.versionCount || 0} · updated {compactWhen(touchedAt(d))}
                {d.aiProfile?.docType ? ` · ${d.aiProfile.docType}` : ''}
              </span>
              {d.objective && (
                <span className="u-xs u-muted" title={d.objective} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  🎯 {d.objective}
                </span>
              )}
              {(d.tags || []).length > 0 && (
                <span className="u-cluster" style={{ '--gap': 'var(--sp-1)', marginTop: 2 }}>
                  {d.tags.map((t) => <span key={t} className="cs-chip u-xs">#{t}</span>)}
                </span>
              )}
            </div>
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
              {d.status === 'ready' && (
                <>
                  <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${scope.deal}/docs/${d.id}?mode=clean`}>Clean</Link>
                  <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${scope.deal}/docs/${d.id}?mode=redline`}>Redline</Link>
                  <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${scope.deal}/docs/${d.id}?mode=sxs`}>Side by side</Link>
                  {isAiConfigured() && docViewOf(d) !== 'deck' && (
                    <button
                      type="button"
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      title="Derive a new agreement from this one (e.g. HoldCo OA → subsidiary OA) — clean + redline, with per-section rationale"
                      onClick={() => setRefactorDoc(d)}
                    >
                      Refactor with AI
                    </button>
                  )}
                  {d.id !== deal.coreDocId && (
                    <button
                      type="button"
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      title="Mark this as the project's starting point — the core document filings and derived drafts relate back to"
                      onClick={() => markCore(d)}
                    >
                      ☆ Set as starting point
                    </button>
                  )}
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="Tag this document — provenance (original / derived), instrument, evidence. Tags index into the project brain."
                    onClick={() => setTagsDoc(d)}
                  >
                    Tags…
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="File this document under a folder — Internal work product vs External (sent to / received from the counterparty)"
                    onClick={() => setFolderDoc(d)}
                  >
                    Folder…
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="Move to a cabinet — Inputs (what came in) or Deliverables (what goes out)"
                    onClick={() => setCabinetDoc(d)}
                  >
                    Cabinet…
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="State this agreement's role in the project's ecosystem — audits and AI edits on it anchor to the role"
                    onClick={() => setRoleDoc(d)}
                  >
                    Role…
                  </button>
                </>
              )}
              <span className={`cs-badge ${DOC_STATUS_TONE[d.status] || ''}`}>{d.status}</span>
            </span>
            </div>
            </div>
            {(d.status === 'processing' || d.status === 'queued') && d.progress && (
              <TaskProgress progress={d.progress} compact />
            )}
          </div>
        ))}
      </div>
      {supersededDocs.length > 0 && (
        <details>
          <summary className="u-small u-muted" style={{ cursor: 'pointer' }}>
            🗂 Superseded drafts ({supersededDocs.length}) — the lineage behind the current documents; every version they carry is preserved
          </summary>
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
            {supersededDocs.map((d) => (
              <div key={d.id} className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)', alignItems: 'flex-start', opacity: 0.85 }}>
                <div className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                    <Link to={`/deals/${scope.deal}/docs/${d.id}`} className="u-small" style={{ textDecoration: 'none' }}>
                      <strong>{d.title}</strong>
                    </Link>
                    <span className="cs-badge b-muted">superseded</span>
                  </span>
                  <span className="u-xs u-muted u-tabular">
                    {d.versionCount || 0} version{(d.versionCount || 0) === 1 ? '' : 's'} · last touched {compactWhen(d.updatedAt || d.createdAt)}
                  </span>
                  {d.supersededBy && titleOf(d.supersededBy) && (
                    <span className="u-xs">
                      → replaced by <Link to={`/deals/${scope.deal}/docs/${d.supersededBy}`}>{titleOf(d.supersededBy)}</Link>
                    </span>
                  )}
                </div>
                <Link className="cs-btn cs-btn-ghost cs-btn-sm" to={`/deals/${scope.deal}/docs/${d.id}`} style={{ flexShrink: 0 }}>
                  Open history
                </Link>
              </div>
            ))}
          </div>
        </details>
      )}
      {artifacts.length > 0 && (
        <details open={artifacts.some((d) => d.kind === 'filing')}>
          <summary className="u-small u-muted" style={{ cursor: 'pointer' }}>
            Filings, sources &amp; artifacts ({artifacts.length}) — certificates, original files, reference prints, and filed correspondence, kept for provenance
          </summary>
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
            {artifacts.map((d) => (
              <div key={d.id} className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)', alignItems: 'flex-start' }}>
                <div className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                    <EmojiCluster doc={d} deal={deal} max={3} size={14} />
                    <Link to={`/deals/${scope.deal}/docs/${d.id}`} className="u-small" style={{ textDecoration: 'none' }}>
                      {d.title}
                    </Link>
                    {d.kind === 'filing' && <span className="cs-badge b-info">filing</span>}
                    {d.folder && <span className="cs-chip u-xs">📁 {d.folder}</span>}
                    {(d.tags || []).map((t) => <span key={t} className="cs-chip u-xs">#{t}</span>)}
                  </span>
                  <span className="u-xs u-muted u-tabular">
                    {ARTIFACT_KIND_LABEL[d.kind] || d.kind} · {docFileType(d) || d.format || '—'} · added {compactWhen(d.createdAt)}
                  </span>
                  {d.relation && (
                    <span className="u-xs" style={{ marginTop: 2 }}>
                      <strong>{d.relation.kind || 'Supporting document'}</strong>
                      {d.relation.note ? ` — ${d.relation.note}` : ''}
                      {' '}·{' '}
                      <Link to={`/deals/${scope.deal}/docs/${d.relation.docId}`}>{d.relation.docTitle}</Link>
                    </span>
                  )}
                </div>
                <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="Tag this document — provenance, instrument, evidence"
                    onClick={() => setTagsDoc(d)}
                  >
                    Tags…
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="File under a folder — Internal work product vs External (sent to / received from the counterparty)"
                    onClick={() => setFolderDoc(d)}
                  >
                    Folder…
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    title="Move to a cabinet — Inputs (what came in) or Deliverables (what goes out)"
                    onClick={() => setCabinetDoc(d)}
                  >
                    Cabinet…
                  </button>
                  <span className={`cs-badge ${DOC_STATUS_TONE[d.status] || ''}`}>{d.status}</span>
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
      <EnvelopesSection scope={scope} participant={participant} />

      {showEnvelope && (
        <EnvelopeBuilder
          scope={scope}
          deal={deal}
          docs={docs || []}
          user={user}
          participant={participant}
          onClose={() => setShowEnvelope(false)}
        />
      )}
      {showCertificate && (
        <CertificateModal scope={scope} deal={deal} docs={docs || []} onClose={() => setShowCertificate(false)} />
      )}
      {showPackage && (
        <ExportPackageModal scope={scope} deal={deal} docs={docs || []} participant={participant} onClose={() => setShowPackage(false)} />
      )}
      {showEmail && <DraftEmailModal scope={scope} onClose={() => setShowEmail(false)} />}
      {tagsDoc && <EditDocTagsModal scope={scope} doc={tagsDoc} participant={participant} onClose={() => setTagsDoc(null)} />}
      {folderDoc && (
        <EditDocFolderModal
          scope={scope}
          doc={folderDoc}
          folders={foldersPresent}
          participant={participant}
          onClose={() => setFolderDoc(null)}
        />
      )}
      {roleDoc && <EditDocRoleModal scope={scope} doc={roleDoc} participant={participant} onClose={() => setRoleDoc(null)} />}
      {cabinetDoc && <EditDocCabinetModal scope={scope} doc={cabinetDoc} participant={participant} onClose={() => setCabinetDoc(null)} />}
      {showUpload && <UploadDocModal scope={scope} deal={deal} participant={participant} onClose={() => setShowUpload(false)} />}
      {showDraft && (
        <DraftWithAiModal
          scope={scope}
          deal={deal}
          initialKind={draftKind}
          onClose={() => { setShowDraft(false); setDraftKind('document'); }}
          onCreated={(docId) => navigate(`/deals/${scope.deal}/docs/${docId}`)}
        />
      )}
      {refactorDoc && (
        <RefactorWithAiModal
          scope={scope}
          sourceDoc={refactorDoc}
          onClose={() => setRefactorDoc(null)}
          onQueued={(targetDeal, docId) => {
            setRefactorDoc(null);
            navigate(`/deals/${targetDeal}/docs/${docId}`);
          }}
        />
      )}
    </div>
  );
}

// ---- Team tab -----------------------------------------------------------------------
function TeamTab({ scope, deal, participant }) {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const toast = useToast();
  const { docs: participants } = useEntityList('participant', scope);
  const { docs: members } = useEntityList('member', { org: scope.org });
  const { can } = useDealPowers(scope.deal);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePartyKey, setInvitePartyKey] = useState('');
  const [inviteRole, setInviteRole] = useState(DEFAULT_DEAL_ROLE);
  const [busy, setBusy] = useState(false);
  const [sentInvites, setSentInvites] = useState(null);

  const parties = deal.parties || [];
  const isOrgAdmin = role === 'admin' || role === 'owner';
  const isOwner = role === 'owner';
  const canManage = can('manage');
  const myMembership = (members || []).find((m) => m.uid === user?.uid) || null;
  // Who may invite an OUTSIDE email onto this project: org admins/owners, or
  // a project admin whose org membership carries canInvite (People & Access).
  const canInviteHere = isOrgAdmin || (canManage && myMembership?.canInvite === true);

  // Invites that point at this project. Admins read every org invite; a
  // member reads only the ones they sent (one query shape per rules line).
  useEffect(() => {
    if (!user || !org?.id || !canInviteHere) { setSentInvites(null); return undefined; }
    let ref = fsdb.collection('invites').where('orgId', '==', org.id);
    if (!isOrgAdmin) ref = ref.where('invitedBy', '==', user.uid);
    const unsub = ref.onSnapshot(
      (snap) => setSentInvites(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((inv) => (inv.projects || []).some((pj) => pj.dealId === scope.deal))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      ),
      () => setSentInvites([])
    );
    return () => unsub();
  }, [user, org?.id, isOrgAdmin, canInviteHere, scope.deal]);

  const addParticipant = async (member, partyKey, dealRole) => {
    try {
      const { default: firebase, db } = await import('../firebaseConfig');
      const batch = db.batch();
      batch.set(db.doc(`orgs/${scope.org}/deals/${scope.deal}/participants/${member.uid}`), {
        uid: member.uid,
        email: member.email,
        displayName: member.displayName || member.email,
        partyKey,
        dealRole,
        addedBy: user.uid,
        createdAt: Date.now(),
      });
      // Visibility handle for plain org members (rules + queries key on it).
      batch.update(db.doc(`orgs/${scope.org}/deals/${scope.deal}`), {
        memberUids: firebase.firestore.FieldValue.arrayUnion(member.uid),
        updatedAt: Date.now(),
      });
      await batch.commit();
      await appendAudit(scope, actorOf(user, participant), 'participant.added', `${member.displayName || member.email} joined as ${partyKey} (${dealRole})`);
      toast('Participant added', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    const addr = inviteEmail.trim().toLowerCase();
    if (!addr) return;
    setBusy(true);
    try {
      const partyKey = invitePartyKey || parties[0]?.key || 'team';
      // Already in the org → no invite needed; add them to the project directly.
      const existing = (members || []).find((m) => (m.email || '').toLowerCase() === addr);
      if (existing) {
        if ((participants || []).some((p) => p.uid === existing.uid)) {
          toast(`${existing.displayName || existing.email} is already on this project`, 'info');
        } else {
          await addParticipant(existing, partyKey, inviteRole);
        }
        setInviteEmail('');
        return;
      }
      // The invite carries this project; the claims worker provisions the
      // participant + memberUids when they accept, so they land here directly.
      await createInvite(org || scope.org, addr, 'member', user, [{ dealId: scope.deal, dealName: deal.name || '', dealRole: inviteRole, partyKey }]);
      await appendAudit(scope, actorOf(user, participant), 'invite.sent', `${addr} invited to this project as ${partyKey} (${inviteRole})`);
      toast(`Invite sent to ${addr} — they land on this project when they accept`, 'ok');
      setInviteEmail('');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (inv) => {
    try {
      const { db } = await import('../firebaseConfig');
      await db.doc(`invites/${inv.id}`).update({ status: 'revoked' });
      await appendAudit(scope, actorOf(user, participant), 'invite.revoked', `Invite to ${inv.email} revoked`);
      toast('Invite revoked', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const setRestricted = async (restricted) => {
    try {
      await updateEntity('deal', { org: scope.org }, scope.deal, { restricted });
      await appendAudit(scope, actorOf(user, participant), 'deal.visibility',
        restricted ? 'Project restricted — org admins see it only if added' : 'Project opened to org admins');
      toast(restricted ? 'Project restricted' : 'Project visible to org admins', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const notOnDeal = (members || []).filter((m) => !(participants || []).some((p) => p.uid === m.uid));
  const inviteStatus = (inv) => (inv.status === 'pending' && inv.expiresAt && inv.expiresAt < Date.now() ? 'expired' : inv.status);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      {(canManage || isOrgAdmin) && (
        <div className="cs-card u-split" style={{ alignItems: 'center' }}>
          <div className="u-stack" style={{ '--gap': '2px' }}>
            <strong className="u-small">Visibility</strong>
            <span className="u-small u-muted">
              {deal.restricted
                ? 'Restricted — only the people below (and the organization owner) can see this project. Org admins must be added like anyone else.'
                : 'Open — everyone added below sees it, and organization admins can see it too.'}
            </span>
          </div>
          {(canManage && (isOwner || !deal.restricted || (deal.memberUids || []).includes(user?.uid))) && (
            <label className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={deal.restricted === true}
                onChange={(e) => setRestricted(e.target.checked)}
                aria-label="Restrict this project from org admins"
              />
              Restricted
            </label>
          )}
        </div>
      )}

      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <strong className="u-small">Deal team</strong>
        {participants === null && <div className="cs-skeleton" style={{ height: 80 }} />}
        {participants && !participants.length && (
          <div className="cs-empty">
            No participants yet — invite teammates below. Each person gets a role (viewer → admin)
            that controls what they can see and do on this project only. <HelpDot topic="team-roles" label="How roles work" />
          </div>
        )}
        {(participants || []).map((p) => (
          <div key={p.id} className="cs-card u-split">
            <div className="u-stack" style={{ '--gap': '2px' }}>
              <strong>{p.displayName || p.email}</strong>
              <span className="u-small u-muted">{p.email}{p.title ? ` · ${p.title}` : ''}</span>
            </div>
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              <span className="cs-chip">{parties.find((x) => x.key === p.partyKey)?.label || p.partyKey}</span>
              <span className="cs-badge">{p.dealRole}</span>
              {canManage && p.uid !== user.uid && (
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  onClick={async () => {
                    const { default: firebase, db } = await import('../firebaseConfig');
                    const batch = db.batch();
                    batch.delete(db.doc(`orgs/${scope.org}/deals/${scope.deal}/participants/${p.id}`));
                    batch.update(db.doc(`orgs/${scope.org}/deals/${scope.deal}`), {
                      memberUids: firebase.firestore.FieldValue.arrayRemove(p.uid),
                      updatedAt: Date.now(),
                    });
                    await batch.commit();
                    toast('Removed from project', 'ok');
                  }}
                >
                  Remove
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      {canManage && notOnDeal.length > 0 && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-small">Add org members to this deal</strong>
          {notOnDeal.map((m) => (
            <AddParticipantRow key={m.uid} member={m} parties={parties} onAdd={addParticipant} />
          ))}
        </div>
      )}

      {canInviteHere && (
        <form className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }} onSubmit={sendInvite}>
          <strong className="u-small">Invite someone new to this project</strong>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            <input
              className="cs-input"
              style={{ flex: 1, minWidth: 220 }}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="counsel@lawfirm.com"
              aria-label="Invite email"
            />
            {parties.length > 0 && (
              <select className="cs-input" value={invitePartyKey || parties[0]?.key || ''} onChange={(e) => setInvitePartyKey(e.target.value)} aria-label="Party">
                {parties.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            )}
            <select className="cs-input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} aria-label="Project role">
              {['viewer', 'commenter', 'editor', 'approver', 'admin'].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="submit" className="cs-btn cs-btn-outline" disabled={busy || !inviteEmail.trim()}>
              Send invite
            </button>
          </div>
          <div className="cs-help">
            They get an email, sign in with this address at {window.location.origin}, and land on this project with the role chosen here — nothing else in {org?.name} becomes visible to them.
          </div>
          {sentInvites && sentInvites.length > 0 && (
            <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
              <span className="u-xs u-muted" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>Invites to this project</span>
              {sentInvites.map((inv) => (
                <div key={inv.id} className="u-split u-small">
                  <span>
                    {inv.email}
                    <span className="u-muted"> · {(inv.projects || []).find((pj) => pj.dealId === scope.deal)?.dealRole || 'editor'}</span>
                  </span>
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                    <span className={`cs-badge ${inviteStatus(inv) === 'pending' ? 'b-info' : inviteStatus(inv) === 'done' || inviteStatus(inv) === 'accepted' ? 'b-ok' : ''}`}>{inviteStatus(inv)}</span>
                    {inv.emailError && <span className="u-xs u-muted" title={inv.emailError}>email failed</span>}
                    {inv.status === 'pending' && (
                      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => revokeInvite(inv)}>Revoke</button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

function AddParticipantRow({ member, parties, onAdd }) {
  const [partyKey, setPartyKey] = useState(parties[0]?.key || '');
  const [dealRole, setDealRole] = useState(DEFAULT_DEAL_ROLE);
  return (
    <div className="cs-card u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
      <span style={{ flex: 1, minWidth: 160 }}>{member.displayName || member.email}</span>
      <select className="cs-input" value={partyKey} onChange={(e) => setPartyKey(e.target.value)} aria-label="Party">
        {parties.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      <select className="cs-input" value={dealRole} onChange={(e) => setDealRole(e.target.value)} aria-label="Deal role">
        {['viewer', 'commenter', 'editor', 'approver', 'admin'].map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onAdd(member, partyKey, dealRole)}>
        Add
      </button>
    </div>
  );
}

// ---- Activity tab ----------------------------------------------------------------------
// The full audit trail: filterable by category, grouped by day, paginated,
// and every event expands in place. Events that produced a version expand
// into the exact changes — the manifest ops with per-op rationale (the
// concept) and a word-level redline (the lines).
const AUDIT_CATEGORIES = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'version', label: 'Versions', match: (ev) => ev.type?.startsWith('version.') || !!ev.meta?.versionId },
  { id: 'doc', label: 'Documents', match: (ev) => /^(doc\.|export\.|verify\.)/.test(ev.type || '') },
  { id: 'sign', label: 'Signatures', match: (ev) => /^envelope\./.test(ev.type || '') },
  { id: 'agent', label: 'AI agent', match: (ev) => /^(agent\.|audit\.|structure\.)/.test(ev.type || '') },
  { id: 'points', label: 'Deal points', match: (ev) => (ev.type || '').startsWith('dealPoint.') },
  { id: 'team', label: 'Team & project', match: (ev) => /^(participant\.|deal\.|invite\.)/.test(ev.type || '') },
];

const auditTone = (type = '') =>
  type.startsWith('version.') ? 'b-ok'
    : type.startsWith('agent.') ? 'b-info'
      : type.startsWith('dealPoint.') ? 'b-warn'
        : type.startsWith('doc.') ? 'b-info'
          : '';

// Short kind label for the row badge — the raw event code ('agent.derived')
// is for machines; the badge answers "what kind of thing happened".
const auditKind = (type = '') =>
  type.startsWith('version.') ? 'version'
    : type.startsWith('agent.') ? 'AI'
      : type.startsWith('dealPoint.') ? 'deal point'
        : type.startsWith('export.') ? 'export'
          : type.startsWith('doc.') ? 'document'
            : 'project';

// ---- Event headlines --------------------------------------------------------
// Raw audit summaries repeat the full document titles and bury the delta at
// the end. describeAudit() restructures each event for the glance test:
//   title — the action, in plain words        ("AI derived a new draft")
//   chips — the size/shape of the change      ("11 redrafted · 3 removed")
//   doc   — which document                    (secondary line)
//   detail — the why/what-else                (secondary line)
const firstQuoted = (s) => (/"([^"]+)"/.exec(s || '') || [])[1] || '';
const nthQuoted = (s, n) => ([...String(s || '').matchAll(/"([^"]+)"/g)][n] || [])[1] || '';

function describeAudit(ev) {
  const t = ev.type || '';
  const s = ev.summary || '';
  const meta = ev.meta || {};
  const who = ev.byName && !ev.byName.startsWith('fast.legal') ? ev.byName : '';
  const base = { title: t, doc: '', detail: s, chips: [], who };

  switch (t) {
    case 'agent.derived': {
      const m = /Derived "([^"]+)" from "([^"]+)"[^:]*:?\s*(.*)$/.exec(s) || [];
      const chips = [];
      const take = (re, label) => { const x = re.exec(m[3] || ''); if (x && +x[1] > 0) chips.push(`${x[1]} ${label}`); };
      take(/(\d+) sections? redrafted/, 'redrafted');
      take(/(\d+) removed/, 'removed');
      take(/(\d+) added/, 'added');
      take(/(\d+) entity renames?/, 'renames');
      return { ...base, title: 'AI derived a new draft', doc: m[1] || firstQuoted(s), detail: m[2] ? `from “${m[2]}”` : '', chips };
    }
    case 'version.published': {
      const v = (/^v(\d+)/.exec(s) || [])[1];
      const label = (/—\s*(.+)$/.exec(s) || [])[1] || '';
      return { ...base, title: 'Version published', doc: firstQuoted(s), detail: label, chips: v ? [`v${v}`] : [] };
    }
    case 'version.sealed': {
      const m = /v(\d+) checkpoint sealed — (\S+) \(chain (\S+)\)/.exec(s) || [];
      return { ...base, title: `Checkpoint sealed${m[1] ? ` — v${m[1]}` : ''}`, detail: '', chips: m[3] ? [`⛓ ${m[3]}`] : [] };
    }
    case 'agent.vibe': {
      const n = (/\((\d+) change/.exec(s) || [])[1];
      return { ...base, title: 'AI edit applied (vibe)', doc: firstQuoted(s), detail: (/\):\s*(.*)$/.exec(s) || [])[1] || '', chips: n ? [`${n} change${n === '1' ? '' : 's'}`] : [] };
    }
    case 'agent.proposal': {
      const n = (/proposed (\d+) change/.exec(s) || [])[1];
      return { ...base, title: 'AI proposed changes', doc: firstQuoted(s), detail: (/\):\s*(.*)$/.exec(s) || [])[1] || '', chips: n ? [`${n} change${n === '1' ? '' : 's'}`] : [] };
    }
    case 'agent.created': {
      const n = (/\((\d+) (?:sections|slides)\)/.exec(s) || [])[1];
      return { ...base, title: 'AI drafted document', doc: firstQuoted(s), detail: (/seeded from .+$/.exec(s) || [])[0] || '', chips: n ? [`${n} sections`] : [] };
    }
    case 'agent.converted': {
      const n = (/\((\d+) slides\)/.exec(s) || [])[1];
      return { ...base, title: 'Converted to presentation', doc: nthQuoted(s, 1), detail: `from “${firstQuoted(s)}”`, chips: n ? [`${n} slides`] : [] };
    }
    case 'agent.ask':
      return {
        ...base,
        title: 'Asked the project brain',
        detail: meta.answer || s,
        chips: meta.sourceCount ? [`${meta.sourceCount} cite${meta.sourceCount === 1 ? '' : 's'}`] : [],
      };
    case 'agent.counsel':
      return {
        ...base,
        title: 'Counsel (Instruct was a question)',
        detail: meta.answer || s,
        chips: meta.sourceCount ? [`${meta.sourceCount} cite${meta.sourceCount === 1 ? '' : 's'}`] : [],
      };
    case 'agent.marketStudy':
      return {
        ...base,
        title: 'Market study filed',
        doc: firstQuoted(s) || meta.docId,
        chips: meta.recCount ? [`${meta.recCount} recommend`] : [],
      };
    case 'agent.questionAnswered':
      return { ...base, title: 'Agent question answered', doc: firstQuoted(s) };
    case 'audit.purpose':
      return {
        ...base, title: 'Purpose audit run', doc: firstQuoted(s), detail: (/—\s*(.*)$/.exec(s) || [])[1] || '',
        chips: [meta.findings ? `${meta.findings} flagged` : '', meta.missing ? `${meta.missing} missing` : ''].filter(Boolean),
      };
    case 'deal.objective':
      return { ...base, title: 'Objective set', doc: '', detail: (/:\s*(.*)$/.exec(s) || [])[1] || '' };
    case 'structure.mapped':
      return {
        ...base, title: 'Company structure mapped', doc: '', detail: (/—\s*(.*)$/.exec(s) || [])[1] || '',
        chips: [meta.entities ? `${meta.entities} entities` : '', meta.agreements ? `${meta.agreements} agreements` : ''].filter(Boolean),
      };
    case 'doc.ingested':
      return {
        ...base, title: 'Document imported', doc: firstQuoted(s), detail: '',
        chips: [meta.format, meta.clauseCount ? `${meta.clauseCount} clauses` : '', meta.aiSuggested ? `${meta.aiSuggested} suggested points` : ''].filter(Boolean),
      };
    case 'doc.newDraft': {
      const v = (/as v(\d+)/.exec(s) || [])[1];
      const from = (/— from (\S+)$/.exec(s) || [])[1];
      return {
        ...base, title: 'New draft received', doc: firstQuoted(s), detail: from ? `from ${from}` : '',
        chips: [v ? `v${v}` : '', meta.opCount ? `${meta.opCount} clause changes` : ''].filter(Boolean),
      };
    }
    case 'doc.newDraftRejected':
      return { ...base, title: 'Draft rejected', doc: firstQuoted(s) };
    case 'doc.uploaded':
      return { ...base, title: 'Document added', doc: firstQuoted(s), detail: '' };
    case 'doc.tagged':
      return { ...base, title: 'Tags updated', doc: firstQuoted(s), detail: (/tagged:\s*(.*)$/.exec(s) || [])[1] || '' };
    case 'doc.coreMarked':
      return { ...base, title: 'Starting point set', doc: firstQuoted(s), detail: '' };
    case 'export.docx': {
      const mode = (/\(([^)]+)\)$/.exec(s) || [])[1];
      return { ...base, title: 'Exported to Word', doc: firstQuoted(s), detail: '', chips: mode ? [mode] : [] };
    }
    case 'export.pptx':
      return { ...base, title: 'Exported to PowerPoint', doc: firstQuoted(s), detail: '' };
    case 'deal.created':
      return { ...base, title: 'Project created', doc: firstQuoted(s), detail: '' };
    case 'participant.added':
      return { ...base, title: 'Team member added' };
    case 'dealPoint.created':
      return { ...base, title: 'Deal point created', doc: firstQuoted(s), detail: '' };
    case 'dealPoint.accepted':
      return { ...base, title: 'AI-suggested point accepted', doc: firstQuoted(s), detail: '' };
    case 'dealPoint.position':
      return { ...base, title: 'Position taken', doc: firstQuoted(s) };
    case 'dealPoint.status':
      return { ...base, title: 'Deal point status changed', doc: firstQuoted(s) };
    case 'dealPoint.locked':
    case 'dealPoint.unlocked':
      return { ...base, title: t === 'dealPoint.locked' ? 'Deal point locked' : 'Deal point unlocked', doc: firstQuoted(s), detail: '' };
    case 'dealPoint.proposalAccepted':
    case 'dealPoint.proposalRejected':
      return { ...base, title: t.endsWith('Accepted') ? 'Proposal accepted' : 'Proposal rejected', doc: firstQuoted(s) };
    default:
      return base;
  }
}

// Checkpoint seals are bookkeeping that follows a substantive change — fold
// each version.sealed event into the nearest event on the same document
// (within 15 min) so the trail reads change-first, with the seal as a chip.
function foldSeals(events) {
  const sorted = [...(events || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const hosts = sorted.filter((e) => e.type !== 'version.sealed');
  const sealsFor = new Map(); // host id → [seal events, oldest first]
  const orphans = [];
  for (const seal of sorted) {
    if (seal.type !== 'version.sealed') continue;
    let best = null;
    let bestDt = Infinity;
    for (const h of hosts) {
      if (!seal.meta?.docId || h.meta?.docId !== seal.meta.docId) continue;
      const dt = Math.abs((h.createdAt || 0) - (seal.createdAt || 0));
      if (dt < bestDt) { best = h; bestDt = dt; }
    }
    if (best && bestDt <= 15 * 60 * 1000) {
      if (!sealsFor.has(best.id)) sealsFor.set(best.id, []);
      sealsFor.get(best.id).push(seal);
    } else {
      orphans.push(seal);
    }
  }
  for (const list of sealsFor.values()) list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  const merged = [...hosts, ...orphans].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return { merged, sealsFor };
}

// Compact timestamp for dense lists: time only today, weekday+time this
// week, date+time this year — never the full locale dump.
function compactWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const startOfDay = (x) => { const y = new Date(x); y.setHours(0, 0, 0, 0); return y; };
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return time;
  if (days < 7) return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  if (d.getFullYear() === now.getFullYear()) return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
  return d.toLocaleDateString([], { year: '2-digit', month: 'short', day: 'numeric' });
}

// One event on one line — the dashboard-density rendering of the same
// describeAudit/foldSeals language the full trail uses. Headline + delta
// chips + folded seal chip, compact time on the right, doc/detail/actor as
// a fitted second line.
function AuditMiniRow({ ev, seals, onClick, className }) {
  const d = describeAudit(ev);
  const secondary = [d.doc && `“${d.doc}”`, d.detail, d.who ? `by ${d.who}` : ''].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      className={`cs-rowlink ${className || ''}`}
      style={{ display: 'block', width: '100%', textAlign: 'left' }}
      onClick={onClick}
      title={ev.summary}
    >
      <span className="u-split" style={{ '--gap': 'var(--sp-2)', flexWrap: 'nowrap', alignItems: 'baseline' }}>
        <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline', minWidth: 0, flexWrap: 'nowrap', overflow: 'hidden' }}>
          <strong style={{ whiteSpace: 'nowrap' }}>{d.title}</strong>
          {d.chips.slice(0, 3).map((c) => <span key={c} className="cs-chip u-xs" style={{ flexShrink: 0 }}>{c}</span>)}
          {(seals || []).length > 0 && (
            <span
              className="cs-chip u-xs"
              style={{ color: 'var(--ok)', flexShrink: 0 }}
              title={seals.map((sl) => `${(/(v\d+)/.exec(sl.summary) || [])[1] || 'v?'} sealed — chain ${sl.meta?.chainHash || ''}`).join('\n')}
            >
              ⛓ {seals.map((sl) => (/(v\d+)/.exec(sl.summary) || [])[1] || 'v?').join('+')}
            </span>
          )}
        </span>
        <span className="u-xs u-muted u-tabular" style={{ flexShrink: 0 }}>{compactWhen(ev.createdAt)}</span>
      </span>
      {secondary && <FitText className="u-xs u-muted" text={secondary} />}
    </button>
  );
}

const dayLabel = (ts) => {
  const d = new Date(ts);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

// The exact changes behind a version-producing event: loads the version doc
// (manifest is always inline — only clauses spill to S3) and renders each op
// with its rationale and a word-level before/after redline.
function AuditChangeDetail({ scope, docId, versionId }) {
  const { doc: version } = useDoc(`orgs/${scope.org}/deals/${scope.deal}/docs/${docId}/versions/${versionId}`);
  const [showAll, setShowAll] = useState(false);
  if (version === undefined) return <div className="cs-skeleton" style={{ height: 60 }} />;
  if (version === null) return <span className="u-small u-muted">This version no longer exists (the document may have been deleted).</span>;

  const m = version.manifest;
  const ops = m?.ops || [];
  const shown = showAll ? ops : ops.slice(0, 6);
  const docBase = `/deals/${scope.deal}/docs/${docId}`;

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-small u-muted">
        <strong>v{version.number}</strong>{version.label ? ` — ${version.label}` : ''} · by {version.authorName}
        {m?.source && <> · source: <strong>{MANIFEST_SOURCE_LABEL[m.source] || m.source}</strong></>}
        {m?.mode === 'vibe' && <span className="cs-chip" style={{ marginLeft: 'var(--sp-2)' }}>vibe — applied without review</span>}
        {m?.instruction && <div style={{ marginTop: 'var(--sp-1)' }}>Instruction: “{String(m.instruction).slice(0, 300)}”</div>}
      </div>
      {!m && (
        <span className="u-small u-muted">
          No manifest on this version — it predates change tracking or was imported.
          The <Link to={`${docBase}?mode=redline&v=${versionId}`}>redline view</Link> still shows every difference against its base.
        </span>
      )}
      {ops.length > 0 && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          {shown.map((op, i) => (
            <div key={`${op.id}-${i}`} className="u-stack" style={{ '--gap': 'var(--sp-1)', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div className="u-small">
                <span className={`cs-badge ${op.op === 'add' ? 'b-ok' : op.op === 'remove' ? 'b-danger' : 'b-info'}`}>{op.op}</span>{' '}
                <strong>{(op.after || op.before)?.heading || op.id}</strong>
              </div>
              {op.rationale && <span className="u-xs u-muted" style={{ fontStyle: 'italic' }}>{op.rationale}</span>}
              {op.op === 'edit' && (
                <div className="u-small" style={{ maxHeight: 160, overflow: 'auto', lineHeight: 1.6 }}>
                  <Redline before={op.before?.text || ''} after={op.after?.text || ''} />
                </div>
              )}
              {op.op === 'add' && (
                <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto', color: 'var(--ok)' }}>{op.after?.text}</div>
              )}
              {op.op === 'remove' && (
                <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                  <del style={{ color: 'var(--danger)', opacity: 0.8 }}>{op.before?.text}</del>
                </div>
              )}
            </div>
          ))}
          {ops.length > shown.length && (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowAll(true)}>
              Show all {ops.length} changes
            </button>
          )}
        </div>
      )}
      {m && !ops.length && <span className="u-small u-muted">Manifest has no ops recorded.</span>}
      <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`${docBase}?v=${versionId}`}>Open at this version</Link>
        <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`${docBase}?mode=redline&v=${versionId}`}>Redline view</Link>
      </span>
    </div>
  );
}

function AuditRow({ scope, ev, seals }) {
  const [open, setOpen] = useState(false);
  const meta = ev.meta || {};
  const d = describeAudit(ev);
  const hasChanges = !!(meta.docId && meta.versionId) && ev.type !== 'version.sealed' && !ev.type.startsWith('export.');
  const extraMeta = Object.entries(meta).filter(
    ([k, v]) => !['docId', 'versionId', 's3Key'].includes(k) && v !== '' && v != null && typeof v !== 'object'
  );
  const secondary = [d.doc, d.detail, d.who ? `by ${d.who}${ev.partyKey ? ` (${ev.partyKey})` : ''}` : '']
    .filter(Boolean);

  return (
    <div className="cs-card u-stack" style={{ '--gap': 0, padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        className="u-split cs-rowlink"
        style={{ padding: 'var(--sp-3) var(--sp-4)', width: '100%', textAlign: 'left', alignItems: 'center', gap: 'var(--sp-3)' }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'flex-start', minWidth: 0, flexWrap: 'nowrap' }}>
          <span className="u-xs u-muted u-tabular" style={{ flexShrink: 0, paddingTop: 2 }}>
            {new Date(ev.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className={`cs-badge ${auditTone(ev.type)}`} style={{ flexShrink: 0 }} title={ev.type}>{auditKind(ev.type)}</span>
          <span className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
            <span className="u-small u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
              <strong>{d.title}</strong>
              {d.chips.map((c) => <span key={c} className="cs-chip u-xs">{c}</span>)}
              {(seals || []).length > 0 && (
                <span
                  className="cs-chip u-xs"
                  style={{ color: 'var(--ok)' }}
                  title={(seals || []).map((sl) => `${(/(v\d+)/.exec(sl.summary) || [])[1] || 'v?'} chain ${sl.meta?.chainHash || ''}`).join('\n')}
                >
                  ⛓ {seals.map((sl) => (/(v\d+)/.exec(sl.summary) || [])[1] || 'v?').join(' + ')} sealed
                </span>
              )}
            </span>
            {secondary.length > 0 && (
              <span className="u-xs u-muted" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: open ? 'normal' : 'nowrap' }}>
                {secondary.join(' · ')}
              </span>
            )}
          </span>
        </span>
        <span className="u-muted" style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>
      </button>
      {open && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)', padding: 'var(--sp-3) var(--sp-4) var(--sp-4)', borderTop: '1px solid var(--border)' }}>
          <span className="u-xs u-muted u-tabular">{new Date(ev.createdAt).toLocaleString()} · {ev.type}{d.who ? '' : ' · automatic'}</span>
          {(seals || []).length > 0 && (
            <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
              {seals.map((sl) => (
                <span key={sl.id} className="u-xs u-tabular" style={{ color: 'var(--ok)' }}>
                  ⛓ {(/(v\d+)/.exec(sl.summary) || [])[1] || 'v?'} sealed{' '}
                  {new Date(sl.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · content{' '}
                  {String(sl.meta?.contentHash || '').slice(0, 16)}… · chain {String(sl.meta?.chainHash || '').slice(0, 16)}…
                </span>
              ))}
            </div>
          )}
          {extraMeta.length > 0 && (
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              {extraMeta.map(([k, v]) => (
                <span key={k} className="cs-chip u-xs">{k}: {String(v).slice(0, 60)}</span>
              ))}
            </span>
          )}
          {(meta.question || meta.answer) && (
            <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
              {meta.question && <span className="u-small"><strong>Asked:</strong> {meta.question}</span>}
              {meta.answer && <span className="u-small" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{meta.answer}</span>}
            </div>
          )}
          {hasChanges ? (
            <AuditChangeDetail scope={scope} docId={meta.docId} versionId={meta.versionId} />
          ) : meta.docId ? (
            <span>
              <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/deals/${scope.deal}/docs/${meta.docId}${meta.versionId ? `?v=${meta.versionId}` : ''}`}>Open document</Link>
            </span>
          ) : !meta.question && !meta.answer ? (
            <span className="u-xs u-muted">No further detail recorded for this event.</span>
          ) : null}
        </div>
      )}
    </div>
  );
}

const AUDIT_PAGE_SIZE = 25;

// Secure shares — view-in-browser links minted for named outsiders
// (backend/api/shareApi.js). Lists every link on the project with its state
// and opens; Revoke kills a link at once. Minting is script-side for now.
function SharesTab({ scope }) {
  const { can } = useDealPowers(scope.deal);
  const toast = useToast();
  const [links, setLinks] = useState(null);
  const [busyId, setBusyId] = useState('');

  const load = async () => {
    try {
      setLinks(await listSecureShares(scope.org, scope.deal));
    } catch (err) {
      toast(err.message, 'danger');
      setLinks([]);
    }
  };
  useEffect(() => { load(); }, [scope.org, scope.deal]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (l) => {
    if (!window.confirm(`Revoke the link shared with ${(l.recipients || []).join(', ')}? It stops working immediately.`)) return;
    setBusyId(l.id);
    try {
      await revokeSecureShare({ orgId: scope.org, dealId: scope.deal, id: l.id });
      toast('Link revoked', 'ok');
      await load();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusyId('');
    }
  };

  const when = (ms) => (ms ? new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const badge = { live: 'b-ok', expired: 'b-muted', revoked: 'b-danger' };

  if (links === null) return <div className="cs-empty">Loading…</div>;
  if (!links.length) {
    return (
      <div className="cs-empty">
        No secure shares on this project yet. A secure share is a view-only link for one named person, gated by a
        one-time code to their email, watermarked with their address, expiring on a date you set and revocable here.
      </div>
    );
  }
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      {links.map((l) => (
        <div key={l.id} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
          <div className="u-split" style={{ alignItems: 'flex-start' }}>
            <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                <strong>{l.title}</strong>
                <span className={`cs-badge ${badge[l.state] || 'b-info'}`}>{l.state}</span>
              </div>
              <div className="u-small">to <strong>{(l.recipients || []).join(', ')}</strong> · {l.files.map((f) => f.name).join(' · ')}</div>
              <div className="u-xs u-muted u-tabular">
                created {when(l.createdAt)}{l.createdByName ? ` by ${l.createdByName}` : ''} · expires {when(l.expiresAt)}
                {l.revokedAt ? ` · revoked ${when(l.revokedAt)}` : ''}
                {' · '}{(l.verified || []).length ? `verified: ${l.verified.join(', ')}` : 'not yet verified'}
                {' · '}{l.opens} open{l.opens === 1 ? '' : 's'}{l.lastOpenAt ? `, last ${when(l.lastOpenAt)}` : ''}
              </div>
            </div>
            {l.state === 'live' && can('manage') && (
              <button className="cs-btn cs-btn-ghost cs-btn-sm" style={{ color: 'var(--danger)' }} disabled={busyId === l.id} onClick={() => revoke(l)}>
                {busyId === l.id ? 'Revoking…' : 'Revoke'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityTab({ scope }) {
  const { docs: events } = useEntityList('audit', scope);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(0);

  // Seals fold into the event that produced the version — the trail reads
  // change-first, with each checkpoint as a chip on its change.
  const { merged, sealsFor } = useMemo(() => foldSeals(events), [events]);

  const filtered = useMemo(() => {
    const cat = AUDIT_CATEGORIES.find((c) => c.id === category);
    if (!cat || cat.id === 'all') return merged;
    return merged.filter((ev) => cat.match(ev) || (cat.id === 'version' && sealsFor.has(ev.id)));
  }, [merged, sealsFor, category]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / AUDIT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageEvents = filtered.slice(safePage * AUDIT_PAGE_SIZE, (safePage + 1) * AUDIT_PAGE_SIZE);

  // Day headers inside the page keep chronology scannable.
  const grouped = useMemo(() => {
    const groups = [];
    for (const ev of pageEvents) {
      const label = dayLabel(ev.createdAt);
      if (!groups.length || groups[groups.length - 1].label !== label) groups.push({ label, events: [] });
      groups[groups.length - 1].events.push(ev);
    }
    return groups;
  }, [pageEvents]);

  const counts = useMemo(() => Object.fromEntries(AUDIT_CATEGORIES.map((c) => [
    c.id,
    c.id === 'all' ? merged.length : merged.filter((ev) => c.match(ev) || (c.id === 'version' && sealsFor.has(ev.id))).length,
  ])), [merged, sealsFor]);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {AUDIT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              className="cs-chip"
              style={{
                cursor: 'pointer',
                ...(category === c.id ? { borderColor: 'var(--info)', fontWeight: 600 } : {}),
                ...(counts[c.id] === 0 && c.id !== 'all' ? { opacity: 0.45 } : {}),
              }}
              onClick={() => { setCategory(c.id); setPage(0); }}
            >
              {c.label} <span className="u-muted u-xs">{counts[c.id] ?? 0}</span>
            </button>
          ))}
        </span>
        <span className="u-xs u-muted">Click any event to expand it — version events show the exact changes.</span>
      </div>

      {events === null && <div className="cs-skeleton" style={{ height: 120 }} />}
      {events && !filtered.length && (
        <div className="cs-empty">
          {category === 'all'
            ? 'The audit trail will record every action on this project.'
            : 'No events in this category yet.'}
        </div>
      )}

      {grouped.map((g) => (
        <div key={g.label} className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>{g.label}</span>
          {g.events.map((ev) => <AuditRow key={ev.id} scope={scope} ev={ev} seals={sealsFor.get(ev.id)} />)}
        </div>
      ))}

      {pageCount > 1 && (
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', justifyContent: 'center', alignItems: 'center' }}>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
            ‹ Newer
          </button>
          <span className="u-small u-muted u-tabular">Page {safePage + 1} of {pageCount} · {filtered.length} events</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
            Older ›
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Dashboard tab ---------------------------------------------------------------------
const PRIORITY_TONE = { critical: 'b-danger', high: 'b-warn', medium: '', low: '' };

// Section identity: every dashboard card opens with an icon tile + a one-line
// purpose, so it's obvious at a glance what each section is FOR.
const ICON_PATHS = {
  doc: <><path d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" /><path d="M13 2v6h6" /></>,
  board: <><rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="12" rx="1" /><rect x="17" y="3" width="5" height="8" rx="1" /></>,
  steps: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  alert: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>,
  comment: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  pulse: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
};

function SectionIcon({ name, tone = 'info' }) {
  return (
    <span className="cs-section-icon" style={{ color: `var(--${tone})`, background: `var(--${tone}-bg)` }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {ICON_PATHS[name]}
      </svg>
    </span>
  );
}

function SectionHead({ icon, tone, title, hint, action, guide }) {
  return (
    <div className="u-split" style={{ alignItems: 'center', gap: 'var(--sp-3)' }}>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center', minWidth: 0 }}>
        <SectionIcon name={icon} tone={tone} />
        <span className="u-stack" style={{ '--gap': '1px', minWidth: 0 }}>
          <strong className="u-small">{title}{guide && <HelpDot topic={guide} />}</strong>
          <span className="u-xs u-muted">{hint}</span>
        </span>
      </span>
      {action}
    </div>
  );
}

// Mini version timeline lives in components/VersionRail.js (shared with the
// document view, where it doubles as a version picker).

function KanbanCard({ point, parties, partyColors, onOpen }) {
  return (
    <button type="button" className="cs-kanban-card u-stack" style={{ '--gap': 'var(--sp-2)' }} onClick={onOpen}>
      <span>{point.title}</span>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-1)', alignItems: 'center' }}>
        {point.priority && point.priority !== 'medium' && (
          <span className={`cs-badge ${PRIORITY_TONE[point.priority] || ''}`}>{point.priority}</span>
        )}
        <span className="u-xs u-muted">{point.category}</span>
        <span style={{ flex: 1 }} />
        {(parties || []).map((p) => {
          const pos = (point.positions || {})[p.key];
          return (
            <span
              key={p.key}
              className="cs-sidebar-dot"
              data-tip={`${p.label}: ${pos ? pos.status : 'no position'}`}
              style={{
                background: pos ? partyColors[p.key] : 'transparent',
                border: `1px solid ${partyColors[p.key]}`,
                opacity: pos ? 1 : 0.5,
              }}
            />
          );
        })}
      </span>
    </button>
  );
}

// ---- Agent questions — answer gaps the AI refused to guess on -------------------------
// Each open question (from a derive/refactor run) renders with the agent's
// proposed answers as one-click chips plus a freeform input. Applying an
// answer runs a targeted vibe-mode edit (new tracked version + manifest op),
// then clears the question from the doc.
// Elapsed ticker for the gap between clicking Apply and the backend's first
// progress flush (~1.5s) — the button must never look hung.
function ApplyingSince({ from }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.round((Date.now() - from) / 1000));
  return (
    <span className="u-xs u-muted u-tabular">⏱ {s}s — reaching the drafting agent…</span>
  );
}

function QuestionRow({ scope, doc, q, idx }) {
  const toast = useToast();
  const { user } = useAuth();
  const text = typeof q === 'string' ? q : q.question;
  const options = (typeof q === 'string' ? [] : q.options) || [];
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [applyStartedAt, setApplyStartedAt] = useState(null);

  const removeQuestion = () =>
    updateEntity('dealDoc', scope, doc.id, {
      agentQuestions: (doc.agentQuestions || []).filter((_, i) => i !== idx),
    });

  const apply = async (e) => {
    e.preventDefault();
    if (!answer.trim()) return;
    setBusy(true);
    setApplyStartedAt(Date.now());
    try {
      await aiDocAgent({
        orgId: scope.org,
        dealId: scope.deal,
        docId: doc.id,
        mode: 'vibe',
        instruction:
          `The refactoring agent left an open question on this document: "${text}". ` +
          `The user's answer: "${answer.trim()}". Apply this answer precisely to the affected section(s) — ` +
          `change nothing else anywhere in the document.`,
      });
      await removeQuestion();
      await appendAudit(scope, actorOf(user, null), 'agent.questionAnswered',
        `Answered agent question on "${doc.title}": ${text.slice(0, 100)} → ${answer.trim().slice(0, 100)}`,
        { docId: doc.id });
      toast('Answer applied — a new version was published with the change', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
    setBusy(false);
    setApplyStartedAt(null);
  };

  return (
    <form className="u-stack" style={{ '--gap': 'var(--sp-2)', padding: 'var(--sp-3)', background: 'var(--warn-bg)', borderRadius: 'var(--radius)' }} onSubmit={apply}>
      <div className="u-small">
        <span className="u-xs u-muted">{doc.title} · </span>
        {text}
      </div>
      {q.evidence && (
        <span className="u-xs" style={{ color: 'var(--ok)' }}>
          📎 Answer found in{' '}
          <Link to={`/deals/${scope.deal}/docs/${q.evidence.docId}`} style={{ color: 'inherit', fontWeight: 600 }}>
            {q.evidence.docTitle}
          </Link>
          {q.evidence.quote ? <> — “{q.evidence.quote}”</> : null}
        </span>
      )}
      {options.length > 0 && (
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {options.map((o) => (
            <button
              key={o}
              type="button"
              className="cs-chip"
              style={{ cursor: 'pointer', ...(answer === o ? { borderColor: 'var(--info)', fontWeight: 600 } : {}) }}
              onClick={() => setAnswer(o)}
              title={o === q.evidence?.answer ? `Backed by "${q.evidence.docTitle}"` : 'Use this proposed answer'}
            >
              {o === q.evidence?.answer && <span aria-hidden="true">📎 </span>}
              {o}
            </button>
          ))}
        </div>
      )}
      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <input
          className="cs-input"
          style={{ flex: 1, minWidth: 200 }}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Answer in plain language, or pick a proposal above"
          aria-label="Your answer"
          disabled={busy}
        />
        <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !answer.trim()}>
          {busy ? 'Applying…' : 'Apply answer'}
        </button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={removeQuestion} disabled={busy} title="Remove without editing the document">
          Dismiss
        </button>
      </div>
      {busy && (doc.progress?.taskId ? (
        // The agent edit registers in the task registry and heartbeats the
        // doc's slim progress field — elapsed clock, history-seeded ETA,
        // phase, and cost, streaming through the docs subscription.
        <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <TaskProgress compact progress={doc.progress} />
          <Link className="u-xs" to={`/activity/${doc.progress.taskId}`}>watch this edit in the cockpit →</Link>
        </div>
      ) : applyStartedAt ? (
        <ApplyingSince from={applyStartedAt} />
      ) : null)}
    </form>
  );
}

// AI-suggested deal points from smart import — triage without leaving the
// dashboard. Accept moves the point into the live register (same flow as the
// Register banner); Dismiss deletes the suggestion. Priority tones come from
// the file-level PRIORITY_TONE map (shared with the kanban cards).
function AiSuggestionsCard({ scope, participant, suggested, onGoTo }) {
  const toast = useToast();
  const { user } = useAuth();
  const [busyId, setBusyId] = useState(null);
  if (!suggested.length) return null;

  const act = async (p, fn, okMsg) => {
    setBusyId(p.id);
    try {
      await fn();
      toast(okMsg, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
    setBusyId(null);
  };

  return (
    <div className="cs-card span-2 u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <SectionHead
        icon="flag"
        tone="info"
        title={`AI-suggested deal points (${suggested.length})`}
        guide="deal-points"
        hint="Material points the smart import flagged for the register — accept to track, dismiss to drop"
        action={<button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo('points')}>Open register →</button>}
      />
      <ScrollZone maxHeight={420} className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {suggested.map((p) => (
          <div key={p.id} className="u-stack" style={{ '--gap': 'var(--sp-2)', padding: 'var(--sp-3)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <div className="u-split" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
              <span className="u-stack" style={{ '--gap': '2px', minWidth: 0 }}>
                <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                  <strong>{p.title}</strong>
                  {p.priority && <span className={`cs-badge ${PRIORITY_TONE[p.priority] || 'b-info'}`}>{p.priority}</span>}
                  {p.category && <span className="cs-chip u-xs">{CATEGORY_LABELS[p.category] || p.category}</span>}
                  {(p.clauseRefs || [])[0]?.heading && <span className="cs-chip u-xs">§ {p.clauseRefs[0].heading}</span>}
                </span>
                {p.detail && <span className="u-xs u-muted" style={{ lineHeight: 1.5 }}>{p.detail}</span>}
              </span>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
                <button
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  disabled={busyId === p.id}
                  onClick={() => act(p, () => acceptSuggestedPoint(scope, actorOf(user, participant), p), 'Added to the register')}
                >
                  Accept
                </button>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={busyId === p.id}
                  onClick={() => act(p, () => deleteEntity('dealPoint', scope, p.id), 'Suggestion dismissed')}
                >
                  Dismiss
                </button>
              </span>
            </div>
          </div>
        ))}
      </ScrollZone>
    </div>
  );
}

function CounselTurnsCard({ audit, onGoTo }) {
  const turns = (audit || [])
    .filter((e) => e.type === 'agent.ask' || e.type === 'agent.counsel' || e.type === 'agent.marketStudy')
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, 8);
  if (!turns.length) return null;
  return (
    <div id="counsel-turns" className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)', borderLeft: '3px solid var(--info)' }}>
      <SectionHead
        icon="comment"
        tone="info"
        title={`Counsel (${turns.length})`}
        guide="command-bar"
        hint="Questions the team asked this project’s brain — including Instruct that was really a question. Market comparisons only answer from these files."
        action={
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo?.('activity')}>
            Full trail
          </button>
        }
      />
      <ScrollZone maxHeight={420} className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {turns.map((e) => (
          <div key={e.id} className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
            <span className="u-xs u-muted">
              {e.byName || 'someone'} · {e.type === 'agent.counsel' ? 'via Instruct' : 'Ask'}
              {e.createdAt ? ` · ${new Date(e.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
            </span>
            <strong className="u-small">{e.meta?.question || e.summary}</strong>
            {e.meta?.answer && (
              <span className="u-small u-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{e.meta.answer}</span>
            )}
          </div>
        ))}
      </ScrollZone>
    </div>
  );
}

function AgentQuestionsCard({ scope, deal, participant, docs }) {
  const [showUpload, setShowUpload] = useState(false);
  const withQuestions = (docs || []).filter((d) => d.status === 'ready' && (d.agentQuestions || []).length > 0);
  // Questions without proposals (older docs stored plain strings): ask the
  // project brain to propose concrete candidates, once per document — the
  // options persist onto the doc and arrive through the live subscription.
  const proposedFor = useRef(new Set());
  const [proposing, setProposing] = useState(0);
  useEffect(() => {
    for (const d of withQuestions) {
      const needs = (d.agentQuestions || []).some((q) => typeof q === 'string' || !(q.options || []).length);
      if (needs && !proposedFor.current.has(d.id)) {
        proposedFor.current.add(d.id);
        setProposing((n) => n + 1);
        aiProposeAnswers({ orgId: scope.org, dealId: scope.deal, docId: d.id })
          .catch(() => {})
          .finally(() => setProposing((n) => n - 1));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withQuestions.map((d) => d.id).join(','), scope.org, scope.deal]);

  if (!withQuestions.length || !isAiConfigured()) return null;
  const count = withQuestions.reduce((n, d) => n + d.agentQuestions.length, 0);
  return (
    <div id="agent-questions" className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)', borderLeft: '3px solid var(--warn)' }}>
      <SectionHead
        icon="alert"
        tone="warn"
        title={`Agent questions (${count})`}
        guide="agent-questions"
        hint="Gaps the AI refused to guess — answer in plain language, pick a proposal, or upload the document that contains the answer (it's scanned against these questions on import)"
        action={
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={() => setShowUpload(true)}
            title="Have a certificate or filing that answers one of these? Upload it — every new document is scanned against the open questions, and found answers appear here with a 📎 evidence link"
          >
            📎 Upload source doc
          </button>
        }
      />
      {proposing > 0 && (
        <span className="u-xs u-muted">✨ proposing answers from project knowledge…</span>
      )}
      <ScrollZone maxHeight={460} className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {withQuestions.map((d) =>
          d.agentQuestions.map((q, i) => (
            <QuestionRow key={`${d.id}-${i}-${typeof q === 'string' ? q : q.question}`} scope={scope} doc={d} q={q} idx={i} />
          ))
        )}
      </ScrollZone>
      {showUpload && (
        <UploadDocModal scope={scope} deal={deal} participant={participant} defaultKind="filing" onClose={() => setShowUpload(false)} />
      )}
    </div>
  );
}

// ---- Key information — what the AI parsed out of the paper ----------------------------
// Aggregates the structured knowledge the pipeline already extracted:
// entity cast (roles → who plays them), document profiles (type, governing
// law, parties, key numbers, summary), and the working draft's defined-term
// graph. Only non-empty groups render.
function KeyInfoCard({ scope, docs, draft }) {
  const graphPath = draft ? `orgs/${scope.org}/deals/${scope.deal}/docs/${draft.id}/graph/current` : null;
  const { doc: graph } = useDoc(graphPath);

  const info = useMemo(() => {
    const all = docs || [];
    // Everything in the main grid describes the CURRENT DRAFT's latest
    // version — entities, jurisdictions, and terms change across refactors,
    // and stale facts presented as current are worse than none. Facts from
    // the source template and earlier documents collapse underneath.
    const fmt = (p) => (typeof p === 'string' ? p : [p.name, p.role].filter(Boolean).join(' — '));

    // Entity cast — prefer the draft's own derive cast; other docs only fill
    // roles the draft doesn't claim.
    const roles = [];
    const seen = new Set();
    const castOrder = [draft, ...all.filter((d) => d !== draft).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))];
    for (const d of castOrder) {
      for (const s of d?.deriveSpec?.entityCast || []) {
        if (s.role && s.value && !seen.has(s.role)) {
          seen.add(s.role);
          // "(unchanged)" is derive-interview bookkeeping, not a fact.
          roles.push({ role: s.role, value: String(s.value).replace(/\s*\((unchanged|no change)\)\s*$/i, '') });
        }
      }
    }
    const p = draft?.aiProfile;
    const governing = p?.governingLaw ? [p.governingLaw] : [];
    const parties = [...new Set((p?.parties || []).map(fmt))];
    const keyNumbers = (p?.keyNumbers || []).slice(0, 8).map((n) =>
      typeof n === 'string'
        ? { label: '', value: n }
        : { label: String(n.label || n.name || ''), value: String(n.value ?? '') }
    );
    const summary = p?.summary || '';
    // Profile freshness: refreshed profiles carry the version they describe;
    // an ingest-era profile on a multi-version doc is stale until the graph
    // worker re-profiles (~1 min after a version lands).
    const profileVersion = p?.versionNumber || (p ? 1 : null);
    const profileStale = !!p && !!draft?.currentVersionId
      && (p.versionId ? p.versionId !== draft.currentVersionId : (draft.versionCount || 1) > 1);
    const terms = (graph?.terms || [])
      .slice()
      .sort((a, b) => (b.usedIn?.length || 0) - (a.usedIn?.length || 0))
      .slice(0, 10);
    const filings = all.filter((d) => d.kind === 'filing');
    // Earlier & source documents — profiles kept, but underneath.
    const earlier = all.filter((d) => d.kind === 'primary' && d.aiProfile && d.id !== draft?.id);
    return { roles, governing, parties, keyNumbers, summary, terms, filings, earlier, profileVersion, profileStale };
  }, [docs, graph, draft]);

  const { roles, governing, parties, keyNumbers, summary, terms, filings, earlier, profileVersion, profileStale } = info;
  if (!roles.length && !governing.length && !parties.length && !keyNumbers.length && !summary && !terms.length && !filings.length && !earlier.length) return null;

  const group = (label, node) => (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
      {node}
    </div>
  );

  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split" style={{ alignItems: 'flex-start' }}>
        <SectionHead
          icon="doc"
          tone="info"
          title="Key information"
          guide="key-facts"
          hint="Entities, roles, and facts of the CURRENT draft — the profile re-runs after every new version, so refactors update these; earlier documents' facts are collapsed below"
        />
        {profileVersion && (
          <span className="cs-chip u-xs u-tabular" style={{ flexShrink: 0 }} title={draft?.aiProfile?.at ? `Profiled ${new Date(draft.aiProfile.at).toLocaleString()}` : undefined}>
            as of v{profileVersion}
          </span>
        )}
      </div>
      {profileStale && (
        <span className="u-xs" style={{ color: 'var(--warn)' }}>
          ⏳ These facts describe v{profileVersion} — a newer version exists; re-profiling runs within ~1 minute of a version landing.
        </span>
      )}
      <ScrollZone maxHeight={480} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }}>
        {roles.length > 0 && group('🎭 Entities & roles', (
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            {roles.map((r) => (
              <div key={r.role} className="u-stack" style={{ '--gap': '1px' }}>
                <span className="u-xs u-muted">{r.role}</span>
                <span className="u-small" style={{ fontWeight: 600, lineHeight: 1.4 }}>{r.value}</span>
              </div>
            ))}
          </div>
        ))}
        {(governing.length > 0 || parties.length > 0) && group('⚖️ Parties & governing law', (
          <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
            {parties.map((p) => <span key={p} className="u-small">{p}</span>)}
            {governing.map((g) => (
              <span key={g} className="u-small"><span className="u-muted">Governing law:</span> <strong>{g}</strong></span>
            ))}
          </div>
        ))}
        {terms.length > 0 && group('📖 Key defined terms', (
          <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            {terms.map((t) => (
              <span key={t.term || t.id} className="cs-chip" title={`Used in ${t.usedIn?.length || 0} section${(t.usedIn?.length || 0) === 1 ? '' : 's'}`}>
                {t.term || t.label}
                {(t.usedIn?.length || 0) > 1 && <span className="u-muted u-xs"> ×{t.usedIn.length}</span>}
              </span>
            ))}
          </div>
        ))}
        {keyNumbers.length > 0 && group('🔢 Key numbers', (
          <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
            {keyNumbers.map((n, i) => (
              <div key={`${n.label}-${i}`} className="u-split u-small" style={{ alignItems: 'baseline', gap: 'var(--sp-3)', flexWrap: 'nowrap' }}>
                <span className="u-muted" style={{ minWidth: 0 }}>{n.label || '·'}</span>
                <strong className="u-tabular" style={{ textAlign: 'right', flexShrink: 0 }}>{n.value}</strong>
              </div>
            ))}
          </div>
        ))}
        {filings.length > 0 && group('🏛️ Filings & certificates', (
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            {filings.map((f) => (
              <span key={f.id} className="u-small u-stack" style={{ '--gap': '1px' }}>
                <Link to={`/deals/${scope.deal}/docs/${f.id}`}>{f.title}</Link>
                {f.relation && (
                  <span className="u-xs u-muted">
                    {f.relation.kind || 'Supporting document'}
                    {f.relation.note ? ` — ${f.relation.note}` : ''}
                  </span>
                )}
                {!f.relation && f.status !== 'ready' && <span className="u-xs u-muted">importing…</span>}
              </span>
            ))}
          </div>
        ))}
      </ScrollZone>
      {summary && group('✨ AI summary', <span className="u-small u-muted" style={{ lineHeight: 1.6 }}>{summary}</span>)}
      {earlier.length > 0 && (
        <details>
          <summary className="u-xs u-muted" style={{ cursor: 'pointer', letterSpacing: '0.06em' }}>
            🗂️ FACTS FROM SOURCE &amp; EARLIER DOCUMENTS ({earlier.length}) — the template and prior drafts this project grew from
          </summary>
          <div className="u-stack" style={{ '--gap': 'var(--sp-3)', marginTop: 'var(--sp-3)' }}>
            {earlier.map((d) => {
              const ep = d.aiProfile;
              return (
                <div key={d.id} className="u-stack u-small" style={{ '--gap': '2px', padding: 'var(--sp-3)', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                    <EmojiCluster doc={d} deal={{ coreDocId: '' }} max={3} size={13} />
                    <Link to={`/deals/${scope.deal}/docs/${d.id}`}>{d.title}</Link>
                    {ep.versionNumber && <span className="u-xs u-muted u-tabular">as of v{ep.versionNumber}</span>}
                  </span>
                  <span className="u-xs u-muted">
                    {[
                      ep.docType,
                      ep.governingLaw ? `governed by ${ep.governingLaw}` : '',
                      (ep.parties || []).slice(0, 3).map((x) => (typeof x === 'string' ? x : x.name)).join(', '),
                    ].filter(Boolean).join(' · ')}
                  </span>
                  {ep.summary && <span className="u-xs u-muted" style={{ lineHeight: 1.5 }}>{String(ep.summary).slice(0, 220)}…</span>}
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

// ---- Document gallery — every doc and artifact as a thumbnail -------------------
// The whole paper trail at a glance: working drafts, the original template,
// filings, prior versions — each as a mini page with its emoji role badges.
// The spotlighted draft already has a thumbnail in the Current Draft card,
// so it's excluded here.
const AUDIT_SEVERITY_TONE = { high: 'b-danger', medium: 'b-warn', low: 'b-muted' };
const AUDIT_TYPE_LABEL = { relic: '🏛 relic', adapt: '✏️ adapt', misplaced: '📦 misplaced' };

// Objectives + purpose audit — the "why does each agreement exist" layer.
// Two levels: the PROJECT structure statement (deal.objective — the entity/
// agreement map) and each agreement's own role (doc.objective). Both feed
// every agent prompt via the ecosystem brief; the audit benchmarks the
// selected agreement against its role, the exemplar form, AND the sibling
// agreements — so relics, gaps, and concepts that belong in a DIFFERENT
// agreement (misplaced/belongsIn) all surface. Fixes stage as a gated proposal.
function ObjectiveCard({ scope, deal, docs, draft }) {
  const toast = useToast();
  const { user } = useAuth();
  const [selId, setSelId] = useState(null);
  const [editingProject, setEditingProject] = useState(false);
  const [projectText, setProjectText] = useState('');
  const [editingRole, setEditingRole] = useState(false);
  const [roleText, setRoleText] = useState('');
  const [busy, setBusy] = useState(false);
  const [auditStartedAt, setAuditStartedAt] = useState(null);
  const [staging, setStaging] = useState(false);
  const [stagedProposal, setStagedProposal] = useState(null);

  const primaries = (docs || []).filter((d) => d.kind === 'primary' && d.status === 'ready' && !isSupersededDoc(d));
  const doc = primaries.find((d) => d.id === selId) || draft;
  if (!doc) return null;

  const audit = doc.purposeAudit;
  const stale = audit && doc.currentVersionId && audit.versionId !== doc.currentVersionId;
  const base = `/deals/${scope.deal}/docs/${doc.id}`;
  const signed = (doc.tags || []).includes('executed');
  const canAudit = !!(doc.objective || deal.objective);
  const bySeverity = { high: 0, medium: 0, low: 0 };
  for (const f of audit?.findings || []) bySeverity[f.severity] += 1;

  const saveProject = async (e) => {
    e.preventDefault();
    try {
      await updateEntity('deal', { org: scope.org }, scope.deal, { objective: projectText.trim() });
      await appendAudit(scope, actorOf(user, null), 'deal.objective',
        `Project structure ${deal.objective ? 'updated' : 'set'}: ${projectText.trim().slice(0, 160)}`);
      setEditingProject(false);
      toast('Project structure saved — every agent pass sees it', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const saveRole = async (e) => {
    e.preventDefault();
    try {
      await updateEntity('dealDoc', scope, doc.id, { objective: roleText.trim() });
      await appendAudit(scope, actorOf(user, null), 'deal.objective',
        `Agreement role ${doc.objective ? 'updated' : 'set'} on "${doc.title}": ${roleText.trim().slice(0, 140)}`, { docId: doc.id });
      setEditingRole(false);
      toast('Role saved — audits and edits on this agreement anchor to it', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const runAudit = async () => {
    setBusy(true);
    setAuditStartedAt(Date.now());
    try {
      const res = await aiPurposeAudit({ orgId: scope.org, dealId: scope.deal, docId: doc.id });
      const n = res.purposeAudit.findings.length + res.purposeAudit.missing.length;
      toast(n ? `Audit complete — ${n} item${n === 1 ? '' : 's'} to review` : 'Audit complete — the draft fits the objective', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
    setBusy(false);
    setAuditStartedAt(null);
  };

  const stageFixes = async () => {
    setStaging(true);
    try {
      const fixLines = audit.findings.map((f) =>
        `- Section [${f.id}] "${f.heading}" (${f.type}${f.belongsIn ? ` — concept owned by "${f.belongsIn}"` : ''}): ${f.fix}`);
      const missLines = audit.missing.filter((m) => m.concept).map((m) =>
        `- ADD "${m.concept}"${m.afterId ? ` after section [${m.afterId}]` : ''}: ${m.why}`);
      const res = await aiDocAgent({
        orgId: scope.org,
        dealId: scope.deal,
        docId: doc.id,
        mode: 'precision',
        instruction:
          `A purpose audit benchmarked this draft against its objective: "${audit.objective}". ` +
          `Apply every fix below exactly — remove relics and misplaced concepts entirely, redraft adapts per the guidance, add the missing provisions:\n` +
          [...fixLines, ...missLines].join('\n'),
      });
      setStagedProposal(res.proposalId || null);
      toast(`${res.ops.length} change${res.ops.length === 1 ? '' : 's'} staged for your review`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
    setStaging(false);
  };

  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)', borderLeft: '3px solid var(--info)' }}>
      <div className="u-split" style={{ alignItems: 'flex-start' }}>
        <SectionHead
          icon="steps"
          tone="info"
          title="🎯 Objectives & purpose audit"
          guide="dashboard"
          hint="The project structure and each agreement's role — the AI drafts against them and audits any agreement for relics, gaps, and concepts that belong in a sibling agreement"
        />
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
          {audit && (
            <span className="cs-chip u-xs u-tabular" title="The version this audit examined">audited v{audit.versionNumber}</span>
          )}
          <button
            type="button"
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={runAudit}
            disabled={busy || !canAudit || !isAiConfigured()}
            title={canAudit ? `Audit "${doc.title}" against its role + the agreement ecosystem` : 'Set a role or project structure first'}
          >
            {busy ? 'Auditing…' : audit ? 'Re-run audit' : 'Run purpose audit'}
          </button>
        </span>
      </div>

      {/* Project structure statement — the entity/agreement map. */}
      <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-split" style={{ alignItems: 'baseline' }}>
          <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>🏗 Project structure</span>
          {!editingProject && (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setProjectText(deal.objective || ''); setEditingProject(true); }}>
              {deal.objective ? 'Edit' : 'Set'}
            </button>
          )}
        </span>
        {editingProject ? (
          <form className="u-stack" style={{ '--gap': 'var(--sp-2)' }} onSubmit={saveProject}>
            <textarea
              className="cs-input"
              rows={3}
              value={projectText}
              onChange={(e) => setProjectText(e.target.value)}
              placeholder={'The entity/agreement map this project serves. E.g. "Netiva is a two-layer structure: Netiva Holdings, LLC (HoldCo) wholly owns Netiva, LLC (Nevada OpCo), both managed by HandsOn Global Management, LLC; consultant equity flows through the Services & Equity Award Agreement."'}
            />
            <div className="u-cluster" style={{ justifyContent: 'flex-end', '--gap': 'var(--sp-2)' }}>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setEditingProject(false)}>Cancel</button>
              <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!projectText.trim()}>Save</button>
            </div>
          </form>
        ) : (
          <p className="u-small" style={{ lineHeight: 1.6, margin: 0 }}>
            {deal.objective || <span className="u-muted">Not set — describe the entities and which agreement lives at which layer.</span>}
          </p>
        )}
      </div>

      {/* The agreement under audit + its role in the ecosystem. */}
      <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-split" style={{ alignItems: 'baseline' }}>
          <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center', minWidth: 0 }}>
            <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0 }}>📜 Agreement</span>
            {primaries.length > 1 ? (
              <select
                className="cs-input"
                style={{ width: 'auto', maxWidth: 340, padding: '2px 8px', fontSize: 'var(--fs-sm)' }}
                value={doc.id}
                onChange={(e) => { setSelId(e.target.value); setEditingRole(false); setStagedProposal(null); }}
                aria-label="Agreement to audit"
              >
                {primaries.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title.slice(0, 60)}{(d.tags || []).includes('executed') ? ' ⚖️ signed' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="u-small" style={{ fontWeight: 600 }}>{doc.title}</span>
            )}
            {signed && <span className="cs-badge b-ok" data-tip="Signed & executed — authoritative context for the other agreements">⚖️ signed</span>}
          </span>
          {!editingRole && (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setRoleText(doc.objective || ''); setEditingRole(true); }}>
              {doc.objective ? 'Edit role' : 'Set role'}
            </button>
          )}
        </span>
        {editingRole ? (
          <form className="u-stack" style={{ '--gap': 'var(--sp-2)' }} onSubmit={saveRole}>
            <textarea
              className="cs-input"
              rows={3}
              value={roleText}
              onChange={(e) => setRoleText(e.target.value)}
              placeholder={'What must THIS agreement be? E.g. "Pure single-member OpCo operating agreement: sole member is Netiva Holdings, LLC, manager-managed by HGM — no Class B members or HoldCo-level provisions."'}
            />
            <div className="u-cluster" style={{ justifyContent: 'flex-end', '--gap': 'var(--sp-2)' }}>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setEditingRole(false)}>Cancel</button>
              <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!roleText.trim()}>Save role</button>
            </div>
          </form>
        ) : (
          <p className="u-small" style={{ lineHeight: 1.6, margin: 0 }}>
            {doc.objective || <span className="u-muted">No role set — the audit falls back to the project structure statement.</span>}
          </p>
        )}
      </div>

      {busy && (doc.progress?.taskId ? (
        <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <TaskProgress compact progress={doc.progress} />
          <Link className="u-xs" to={`/activity/${doc.progress.taskId}`}>watch this audit in the cockpit →</Link>
        </div>
      ) : auditStartedAt ? (
        <ApplyingSince from={auditStartedAt} />
      ) : null)}

      {audit && !busy && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          {stale && (
            <span className="u-xs" style={{ color: 'var(--warn)' }}>
              ⏳ This audit examined v{audit.versionNumber} — the draft has moved on; re-run for current findings.
            </span>
          )}
          <p className="u-small" style={{ lineHeight: 1.6, margin: 0 }}>{audit.verdict}</p>
          {audit.exemplar && (
            <details>
              <summary className="u-xs u-muted" style={{ cursor: 'pointer' }}>The exemplar standard this audit benchmarked against</summary>
              <p className="u-xs u-muted" style={{ lineHeight: 1.6, marginTop: 'var(--sp-2)' }}>{audit.exemplar}</p>
            </details>
          )}
          {(audit.findings.length > 0 || audit.missing.length > 0) && (
            <div className="u-split" style={{ alignItems: 'center' }}>
              <span className="u-cluster u-xs" style={{ '--gap': 'var(--sp-2)' }}>
                {bySeverity.high > 0 && <span className="cs-badge b-danger">{bySeverity.high} high</span>}
                {bySeverity.medium > 0 && <span className="cs-badge b-warn">{bySeverity.medium} medium</span>}
                {bySeverity.low > 0 && <span className="cs-badge b-muted">{bySeverity.low} low</span>}
                {audit.missing.length > 0 && <span className="cs-badge b-info">{audit.missing.length} missing</span>}
              </span>
              {stagedProposal ? (
                <Link className="cs-btn cs-btn-primary cs-btn-sm" to={base}>Review staged changes →</Link>
              ) : (
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={stageFixes} disabled={staging || stale}>
                  {staging ? 'Staging…' : 'Stage all fixes for review'}
                </button>
              )}
            </div>
          )}
          {staging && doc.progress?.taskId && <TaskProgress compact progress={doc.progress} />}
          <ScrollZone maxHeight={380} className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            {audit.findings.map((f) => (
              <Link
                key={f.id}
                to={`${base}?clause=${f.id}`}
                className="u-stack"
                style={{ '--gap': '2px', textDecoration: 'none', color: 'inherit', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}
                title="Open this section in the document"
              >
                <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                  <span className={`cs-badge ${AUDIT_SEVERITY_TONE[f.severity]}`}>{f.severity}</span>
                  <span className="cs-chip u-xs">{AUDIT_TYPE_LABEL[f.type] || f.type}</span>
                  {f.belongsIn && (
                    <span className="cs-chip u-xs" style={{ color: 'var(--info)' }} title="The agreement in this project that owns this concept">
                      → belongs in “{f.belongsIn}”
                    </span>
                  )}
                  <strong>{f.heading}</strong>
                </span>
                <span className="u-xs u-muted">{f.issue}</span>
                <span className="u-xs">→ {f.fix}</span>
              </Link>
            ))}
            {audit.missing.filter((m) => m.concept).map((m) => (
              <div key={m.concept} className="u-stack" style={{ '--gap': '2px', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' }}>
                <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                  <span className="cs-badge b-info">missing</span>
                  <strong>{m.concept}</strong>
                </span>
                <span className="u-xs u-muted">{m.why}</span>
              </div>
            ))}
          </ScrollZone>
        </div>
      )}
      {!audit && !busy && canAudit && (
        <span className="u-xs u-muted">
          No audit yet on this agreement — run one to benchmark it against its role, the exemplar form, and the sibling agreements.
        </span>
      )}
    </div>
  );
}

// ---- Company structure map -------------------------------------------------------
// The visual proof that fast.legal is apprised of the company's shape: entity
// layers (HoldCo above OpCo), ownership/management/service edges, and the
// agreement network between entities — compiled by /legal/structure from the
// structure statement, agreement profiles, entity casts, and filings.
function entityEmoji(e) {
  const r = (e.role || '').toLowerCase();
  if (r.includes('holding')) return '🏛';
  if (r.includes('manage')) return '⚙️';
  if (r.includes('consult') || r.includes('contract')) return '🧑\u200d💼';
  if (e.type === 'person') return '👤';
  if (e.type === 'firm') return '🏬';
  return '🏢';
}
const AGR_STATUS_TONE = { signed: 'b-ok', working: 'b-info', superseded: 'b-muted', referenced: 'b-warn' };
const AGR_COLOR = { signed: 'var(--ok)', working: 'var(--info)', referenced: 'var(--warn)', superseded: 'var(--muted)' };
const AGR_EMOJI = { signed: '⚖️', working: '📝', referenced: '📄', superseded: '🗂' };
const LINK_COLOR = { ownership: 'var(--info)', management: 'var(--muted)', service: 'var(--muted)' };
const LINK_DASH = { ownership: '', management: '7 4', service: '2 4' };

function StructureMapCard({ scope, deal, participant }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const containerRef = useRef(null);
  const boxRefs = useRef(new Map());
  const pillRefs = useRef(new Map());
  const [edges, setEdges] = useState([]);
  const [agrNodes, setAgrNodes] = useState([]);
  // The compiled map is an object ({entities, links, agreements}); anything
  // else in the field (e.g. a prose statement written by an agent/script)
  // must degrade to "no map yet", never crash the page.
  const s = deal.structure && typeof deal.structure === 'object' && !Array.isArray(deal.structure)
    ? deal.structure
    : null;

  // Measured layout, not force layout: entity boxes live in normal document
  // flow (ownership spine in layer rows, pure service providers in a side
  // rail) and everything drawn — edges, labels, agreement pills — is computed
  // from the boxes' real positions on every resize. Edges that share a box
  // side spread out along it; labels that would collide stagger downward.
  useLayoutEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      if (!c || !s?.entities?.length) { setEdges([]); setAgrNodes([]); return; }
      const crect = c.getBoundingClientRect();
      const box = {};
      for (const e of s.entities) {
        const el = boxRefs.current.get(e.id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        box[e.id] = { left: r.left - crect.left, top: r.top - crect.top, w: r.width, h: r.height };
      }
      const cx = (b) => b.left + b.w / 2;
      const cy = (b) => b.top + b.h / 2;

      // Each edge leaves/enters on the side facing the other box (dominant
      // axis) — except SAME-ROW neighbors, which arc over the box tops: the
      // gap between adjacent boxes is far too narrow for a line + label.
      const ends = [];
      for (const l of s.links || []) {
        const a = box[l.from];
        const b = box[l.to];
        if (!a || !b) continue;
        const dx = cx(b) - cx(a);
        const dy = cy(b) - cy(a);
        const sameRow = Math.abs(cy(a) - cy(b)) < Math.min(a.h, b.h) / 2;
        const vertical = !sameRow && Math.abs(dy) >= Math.abs(dx);
        ends.push({
          l,
          shape: sameRow ? 'arc' : vertical ? 'v' : 'h',
          fromSide: sameRow ? 'top' : vertical ? (dy > 0 ? 'bottom' : 'top') : (dx > 0 ? 'right' : 'left'),
          toSide: sameRow ? 'top' : vertical ? (dy > 0 ? 'top' : 'bottom') : (dx > 0 ? 'left' : 'right'),
          slotFrom: {},
          slotTo: {},
        });
      }
      // Anchors sharing a box side spread across it instead of stacking on
      // the center — ordered by where the other end sits, so edges don't cross.
      const groups = new Map();
      const addSlot = (id, side, sortKey, slot) => {
        const k = `${id}:${side}`;
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push({ sortKey, slot });
      };
      for (const e of ends) {
        const to = box[e.l.to];
        const from = box[e.l.from];
        addSlot(e.l.from, e.fromSide, ['top', 'bottom'].includes(e.fromSide) ? cx(to) : cy(to), e.slotFrom);
        addSlot(e.l.to, e.toSide, ['top', 'bottom'].includes(e.toSide) ? cx(from) : cy(from), e.slotTo);
      }
      for (const arr of groups.values()) {
        arr.sort((p, q) => p.sortKey - q.sortKey);
        arr.forEach((p, i) => { p.slot.frac = (i + 1) / (arr.length + 1); });
      }
      const pt = (id, side, frac) => {
        const b = box[id];
        if (side === 'top') return { x: b.left + b.w * frac, y: b.top };
        if (side === 'bottom') return { x: b.left + b.w * frac, y: b.top + b.h };
        if (side === 'left') return { x: b.left, y: b.top + b.h * frac };
        return { x: b.left + b.w, y: b.top + b.h * frac };
      };
      const nextEdges = ends.map((e) => {
        const p1 = pt(e.l.from, e.fromSide, e.slotFrom.frac);
        const p2 = pt(e.l.to, e.toSide, e.slotTo.frac);
        // Label placement per shape: arcs label at the apex (open air above
        // the row); vertical edges hug the source end (the middle belongs to
        // the agreement bands); long horizontal rail edges label near source.
        let lx; let ly; let apexY = 0;
        if (e.shape === 'arc') {
          apexY = Math.min(p1.y, p2.y) - 26;
          lx = (p1.x + p2.x) / 2;
          ly = apexY - 4;
        } else if (e.shape === 'v') {
          lx = p1.x + (p2.x - p1.x) * 0.18;
          ly = p1.y + Math.sign(p2.y - p1.y) * 15;
        } else {
          lx = p1.x + (p2.x - p1.x) * 0.3;
          ly = p1.y + (p2.y - p1.y) * 0.3 - 7;
        }
        return { ...e.l, shape: e.shape, apexY, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, lx, ly };
      });
      const labeled = nextEdges.filter((e) => e.label).sort((a, b) => a.ly - b.ly);
      for (let i = 1; i < labeled.length; i += 1) {
        for (let j = 0; j < i; j += 1) {
          if (Math.abs(labeled[i].lx - labeled[j].lx) < 100 && Math.abs(labeled[i].ly - labeled[j].ly) < 14) {
            labeled[i].ly = labeled[j].ly + 15;
          }
        }
      }

      // Agreement pills live IN the layout (band rows between entity layers),
      // so here we only measure them and draw a dashed spoke to each party.
      const pillLines = [];
      for (const [key, el] of pillRefs.current) {
        const a = (s.agreements || [])[key];
        if (!a || !el) continue;
        const r = el.getBoundingClientRect();
        const px = r.left - crect.left + r.width / 2;
        const py = r.top - crect.top + r.height / 2;
        const spokes = (a.between || []).filter((id) => box[id]).map((id) => {
          const b = box[id];
          const dx = px - cx(b);
          const dy = py - cy(b);
          return Math.abs(dx) * b.h > Math.abs(dy) * b.w
            ? { x2: dx > 0 ? b.left + b.w : b.left, y2: cy(b) }
            : { x2: cx(b), y2: dy > 0 ? b.top + b.h : b.top };
        });
        pillLines.push({ status: a.status, x: px, y: py, spokes });
      }
      setEdges(nextEdges);
      setAgrNodes(pillLines);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [s]);

  const build = async () => {
    setBusy(true);
    try {
      const res = await aiStructureMap({ orgId: scope.org, dealId: scope.deal });
      toast(`Structure mapped — ${res.structure.entities.length} entities, ${res.structure.agreements.length} agreements`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
    setBusy(false);
  };

  if (!s && !isAiConfigured()) return null;

  // Ownership spine vs side rail: entities in an ownership chain stack in
  // layer rows; pure managers/service providers (never an owner or owned)
  // stand in a rail on the right so their dashed edges don't tangle the spine.
  const allLinks = s?.links || [];
  const ownershipTouched = new Set();
  for (const l of allLinks) {
    if (l.kind === 'ownership') { ownershipTouched.add(l.from); ownershipTouched.add(l.to); }
  }
  const isSide = (e) => !ownershipTouched.has(e.id) && allLinks.some((l) => l.from === e.id && l.kind !== 'ownership');
  const spine = (s?.entities || []).filter((e) => !isSide(e));
  const side = (s?.entities || []).filter(isSide);
  const layers = new Map();
  for (const e of spine) {
    if (!layers.has(e.layer || 0)) layers.set(e.layer || 0, []);
    layers.get(e.layer || 0).push(e);
  }
  const layerKeys = [...layers.keys()].sort((a, b) => a - b);
  const nameOf = Object.fromEntries((s?.entities || []).map((e) => [e.id, e.name]));
  const entityById = Object.fromEntries((s?.entities || []).map((e) => [e.id, e]));
  // Same-row links arc over the box tops — give the top row headroom for them.
  const hasArc = allLinks.some((l) => {
    const a = entityById[l.from];
    const b = entityById[l.to];
    return a && b && !isSide(a) && !isSide(b) && (a.layer || 0) === (b.layer || 0);
  });

  // Agreements render in BAND rows between the entity layers they connect —
  // part of the normal layout, so a pill can never cover a box or an edge
  // label. A pill slots under the highest layer its parties touch.
  const layerOfEntity = Object.fromEntries(spine.map((e) => [e.id, e.layer || 0]));
  const bands = new Map();
  (s?.agreements || []).forEach((a, idx) => {
    if (a.status === 'superseded' || (a.between || []).length < 2) return;
    const partyLayers = (a.between || []).map((id) => layerOfEntity[id]).filter((x) => x !== undefined);
    const band = partyLayers.length ? Math.min(...partyLayers) : layerKeys[0];
    const key = layerKeys.includes(band) ? band : layerKeys[0];
    if (!bands.has(key)) bands.set(key, []);
    bands.get(key).push({ ...a, idx });
  });

  const entityBox = (e) => (
    <div
      key={e.id}
      ref={(el) => { if (el) boxRefs.current.set(e.id, el); else boxRefs.current.delete(e.id); }}
      className="u-stack"
      style={{
        '--gap': '1px',
        padding: 'var(--sp-2) var(--sp-4)',
        background: 'var(--bg-inset)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius)',
        maxWidth: 230,
        minWidth: 150,
        textAlign: 'center',
        alignItems: 'center',
      }}
      title={e.note || e.role}
    >
      <span style={{ fontSize: 18 }} aria-hidden="true">{entityEmoji(e)}</span>
      <span className="u-small" style={{ fontWeight: 700, lineHeight: 1.3 }}>{e.name}</span>
      <span className="u-xs u-muted">{e.role}</span>
    </div>
  );

  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split" style={{ alignItems: 'flex-start' }}>
        <SectionHead
          icon="board"
          tone="info"
          title="🏗 Company structure"
          guide="structure-map"
          hint="The entity layers, ownership and management links, and the agreement network this project is built on — every targeted AI task works with this picture"
        />
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
          {s?.at && (
            <span className="cs-chip u-xs u-tabular" title={`Compiled by ${s.model || 'AI'} from ${s.sources?.primaries || 0} agreements + ${s.sources?.filings || 0} filings`}>
              as of {new Date(s.at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </span>
          )}
          {isAiConfigured() && (
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={build} disabled={busy}>
              {busy ? 'Mapping…' : s ? 'Refresh map' : 'Map structure'}
            </button>
          )}
        </span>
      </div>

      {!s && (
        <span className="u-small u-muted">
          No map yet — the AI compiles the entity layers and agreement network from the structure statement,
          the agreements' profiles, and any filings. Run it once documents are in.
        </span>
      )}

      {s && (
        <div ref={containerRef} style={{ position: 'relative', padding: `${hasArc ? 46 : 16}px var(--sp-2) var(--sp-4)` }}>
          <svg
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 0 }}
          >
            {/* Agreement spokes sit under the structural edges. */}
            {agrNodes.map((p, i) => p.spokes.map((sp, j) => (
              <line
                key={`agr-${i}-${j}`}
                x1={p.x} y1={p.y} x2={sp.x2} y2={sp.y2}
                stroke={AGR_COLOR[p.status]} strokeWidth="1" strokeDasharray="2 4" opacity="0.45"
              />
            )))}
            {edges.map((e, i) => {
              const midY = (e.y1 + e.y2) / 2;
              const midX = (e.x1 + e.x2) / 2;
              // Quadratic control point that puts the arc's apex at apexY.
              const arcCy = 2 * e.apexY - (e.y1 + e.y2) / 2;
              const d = e.shape === 'arc'
                ? `M ${e.x1} ${e.y1} Q ${midX} ${arcCy}, ${e.x2} ${e.y2}`
                : e.shape === 'v'
                  ? `M ${e.x1} ${e.y1} C ${e.x1} ${midY}, ${e.x2} ${midY}, ${e.x2} ${e.y2}`
                  : `M ${e.x1} ${e.y1} C ${midX} ${e.y1}, ${midX} ${e.y2}, ${e.x2} ${e.y2}`;
              return (
                <g key={i}>
                  <path d={d} fill="none" stroke={LINK_COLOR[e.kind]} strokeWidth={e.kind === 'ownership' ? 2 : 1.4} strokeDasharray={LINK_DASH[e.kind]} opacity="0.8" />
                  <circle cx={e.x2} cy={e.y2} r="3" fill={LINK_COLOR[e.kind]} opacity="0.8" />
                </g>
              );
            })}
          </svg>

          {/* Labels live in their own layer ABOVE the boxes — a wide label
              near a box border must never be chopped by it. */}
          <svg
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 2 }}
          >
            {edges.filter((e) => e.label).map((e, i) => (
              <text
                key={i}
                x={e.lx} y={e.ly}
                textAnchor="middle" fontSize="10" fill="var(--muted)"
                style={{ paintOrder: 'stroke', stroke: 'var(--bg-elev)', strokeWidth: 4 }}
              >
                {e.label}
              </text>
            ))}
          </svg>

          <div style={{ display: 'flex', gap: 'var(--sp-8)', alignItems: 'stretch', position: 'relative', zIndex: 1 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {layerKeys.map((layer, li) => {
                const prev = layerKeys[li - 1];
                const afterBand = li > 0 && (bands.get(prev) || []).length > 0;
                return (
                  <React.Fragment key={layer}>
                    <div
                      className="u-cluster"
                      style={{ '--gap': 'var(--sp-7)', justifyContent: 'center', alignItems: 'stretch', marginTop: li === 0 ? 0 : afterBand ? 14 : 44 }}
                    >
                      {layers.get(layer).map(entityBox)}
                    </div>
                    {(bands.get(layer) || []).length > 0 && (
                      // The agreement band: pills in normal flow between the
                      // layers they connect — they can't cover boxes or labels.
                      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', justifyContent: 'center', marginTop: 30 }}>
                        {bands.get(layer).map((a) => {
                          const pillStyle = {
                            display: 'inline-block',
                            maxWidth: 190,
                            padding: '2px 10px',
                            background: 'var(--bg-elev)',
                            border: `1px ${a.status === 'referenced' ? 'dashed' : 'solid'} ${AGR_COLOR[a.status]}`,
                            borderRadius: 999,
                            fontSize: 'var(--fs-xs)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: 'inherit',
                            textDecoration: 'none',
                          };
                          const setPillRef = (el) => {
                            if (el) pillRefs.current.set(a.idx, el);
                            else pillRefs.current.delete(a.idx);
                          };
                          const label = `${AGR_EMOJI[a.status]} ${a.title}`;
                          return a.docId ? (
                            <Link
                              key={a.idx}
                              ref={setPillRef}
                              to={`/deals/${scope.deal}/docs/${a.docId}`}
                              style={pillStyle}
                              title={`${a.title} — ${a.status}${a.note ? ` · ${a.note}` : ''} · open the document`}
                            >
                              {label}
                            </Link>
                          ) : (
                            // No document behind this pill yet — it doubles as
                            // the upload slot, pre-titled and pre-tagged.
                            <button
                              key={a.idx}
                              ref={setPillRef}
                              type="button"
                              onClick={() => setUploadFor(a)}
                              style={{ ...pillStyle, cursor: 'pointer', opacity: a.status === 'referenced' ? 0.9 : 1 }}
                              title={`${a.title} — not uploaded yet${a.note ? ` · ${a.note}` : ''} · click to upload it into this slot`}
                            >
                              {label} <span aria-hidden="true">⬆︎</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {side.length > 0 && (
              <div className="u-stack" style={{ '--gap': 'var(--sp-6)', justifyContent: 'center', paddingRight: 'var(--sp-3)' }}>
                {side.map(entityBox)}
              </div>
            )}
          </div>
        </div>
      )}

      {s && agrNodes.length > 0 && (
        <span className="u-xs u-muted" style={{ textAlign: 'center' }}>
          ⚖️ signed · 📝 working draft · 📄 referenced — pills without a document behind them are upload slots:
          click one to file that agreement (pre-tagged), then "Refresh map" links it in.
        </span>
      )}

      {uploadFor && (
        <UploadDocModal
          scope={scope}
          deal={deal}
          participant={participant}
          defaultKind="primary"
          defaultTitle={uploadFor.title}
          defaultTags={uploadFor.status === 'signed' ? ['executed'] : []}
          onClose={() => {
            setUploadFor(null);
            toast('Once the import finishes, tap "Refresh map" to link it into the diagram', 'info');
          }}
        />
      )}

      {s && (s.agreements || []).length > 0 && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>🤝 Agreements between the entities</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--sp-3)' }}>
            {s.agreements.map((a, i) => {
              const body = (
                <>
                  <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                    <span className={`cs-badge ${AGR_STATUS_TONE[a.status] || ''}`}>{a.status}</span>
                    <strong style={{ minWidth: 0 }}>{a.title}</strong>
                  </span>
                  {(a.between || []).length > 0 && (
                    <span className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
                      {a.between.map((id) => <span key={id} className="cs-chip u-xs">{nameOf[id] || id}</span>)}
                    </span>
                  )}
                  {a.note && <span className="u-xs u-muted">{a.note}</span>}
                </>
              );
              const style = { '--gap': '3px', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--bg-inset)', borderRadius: 'var(--radius)' };
              return a.docId ? (
                <Link key={i} to={`/deals/${scope.deal}/docs/${a.docId}`} className="u-stack" style={{ ...style, textDecoration: 'none', color: 'inherit' }} title="Open this agreement">
                  {body}
                </Link>
              ) : (
                <div key={i} className="u-stack" style={style} title={a.status === 'referenced' ? 'Known to exist but not uploaded to the project yet' : undefined}>
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Document tree — the meeting-prep surface --------------------------------------
// A diagram, not a list: the KEY DOCUMENT is the root, and every other
// document hangs off it on connector rails, grouped by what it IS —
// negotiation record, working drafts, filings, exhibits, references,
// history. Every row shows the latest version + when it moved, and
// downloads the current version directly — no hunting before a meeting.
const NEGOTIATION_TAGS = new Set(['negotiation', 'open-items', 'deal-points', 'correspondence', 'redline', 'received']);

function treeBranchOf(d) {
  const cab = cabinetOf(d);
  if (cab === 'deliverable') return 'deliverables';
  if (cab === 'input') return 'inputs';
  const tags = (d.tags || []).map((t) => String(t).toLowerCase());
  if (isSupersededDoc(d) || d.kind === 'prior' || tags.includes('original') || tags.includes('source-template')) return 'history';
  if (d.kind === 'email' || tags.some((t) => NEGOTIATION_TAGS.has(t))) return 'negotiation';
  if (d.kind === 'primary') return 'working';
  if (d.kind === 'filing') return 'filings';
  if (d.kind === 'exhibit') return 'exhibits';
  return 'references';
}

const TREE_BRANCHES = [
  { key: 'deliverables', label: '📤 Deliverables', tip: 'What goes out — the files actually sent, kept exactly as built' },
  { key: 'inputs', label: '📥 Inputs', tip: 'What came in — data exports, invoices, hours files, received drafts' },
  { key: 'negotiation', label: '🤝 Negotiation record', tip: 'Open item lists, stance summaries, redlines, and correspondence — what each side said and where things stand' },
  { key: 'working', label: '📜 Working documents', tip: 'Other live drafts and agreements in this project' },
  { key: 'filings', label: '🏛️ Filings & certificates', tip: 'Articles, certificates, and other evidence on record' },
  { key: 'exhibits', label: '📎 Exhibits & schedules', tip: 'Attachments that travel with the agreement' },
  { key: 'references', label: '📚 References & artifacts', tip: 'Generated outputs and background material the team and the AI work from' },
  { key: 'history', label: '🗄️ Sources & history', tip: 'The original this project grew from, prior markups, and superseded drafts — lineage, not day-to-day work' },
];

function DocTreeRow({ scope, deal, d, isKey, canDownload, toast }) {
  const [busy, setBusy] = useState(null); // 'docx' | 'pdf' | 'file' | null
  // Received files and generated artifacts (redlines, comparison PDFs, filings)
  // must download as the ORIGINAL bytes — a clean re-export would silently strip
  // their tracked changes / markup. Only the working draft re-exports from the
  // clause model.
  // Cabinet docs (inputs / deliverables) are files, whatever their kind — an
  // input IS its upload, a deliverable is the exact bytes pinned on its
  // checkpoint (or the upload). The model export never stands in for them.
  const cab = cabinetOf(d);
  const { doc: cabVersion } = useDoc(
    cab === 'deliverable' && d.currentVersionId ? `orgs/${scope.org}/deals/${scope.deal}/docs/${d.id}/versions/${d.currentVersionId}` : ''
  );
  const pinned = cab === 'deliverable' ? (cabVersion?.files || []).filter((f) => f.role === 'reference' && f.s3Key) : [];
  const isFileArtifact = !!d.s3Key && (!!cab || (d.kind !== 'primary' && docViewOf(d) !== 'deck' && docViewOf(d) !== 'workbook'));
  // Workbooks re-export the CURRENT version's model as styled .xlsx — the
  // uploaded original is only v1; edited versions live in the model.
  const isWorkbook = docViewOf(d) === 'workbook';
  // Tracked-changes files open straight into redline view — that IS the content.
  const base = `/deals/${scope.deal}/docs/${d.id}${d.trackedChanges ? '?mode=redline' : ''}`;
  const isDeck = docViewOf(d) === 'deck';
  const ready = d.status === 'ready';
  const grab = async (format) => {
    setBusy(format);
    try {
      await downloadWordExport({ orgId: scope.org, dealId: scope.deal, docId: d.id, mode: 'clean', format });
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(null);
    }
  };
  const grabOriginal = async () => {
    setBusy('file');
    try {
      const ext = (d.s3Key.match(/\.\w+$/) || [''])[0];
      const safe = String(d.title || 'document').replace(/[\\/:*?"<>|]+/g, '');
      await downloadAsset({ key: d.s3Key, name: `${safe}${ext}` });
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="u-split" style={{ gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center', minWidth: 0, flex: 1, flexWrap: 'nowrap' }}>
        <DocThumb doc={d} deal={deal} to={base} size={isKey ? 48 : 38} />
        <span className="u-stack" style={{ '--gap': '1px', minWidth: 0 }}>
          <span className="u-cluster u-small" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline', flexWrap: 'nowrap', minWidth: 0 }}>
            <Link to={base} style={{ fontWeight: isKey ? 700 : 500, fontSize: isKey ? 'var(--fs-md)' : undefined, color: 'inherit', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.title}>
              {d.title}
            </Link>
            {isKey && <span className="cs-badge b-info" style={{ flexShrink: 0 }}>key document</span>}
            {d.id === deal.coreDocId && !isKey && <span className="cs-badge" style={{ flexShrink: 0 }}>★ starting point</span>}
            <EmojiCluster doc={d} deal={deal} max={3} size={13} />
          </span>
          <span className="u-xs u-muted u-tabular">
            {ARTIFACT_KIND_LABEL[d.kind] || (isDeck ? 'presentation' : 'working draft')}
            {docFileType(d) ? ` · ${docFileType(d)}` : ''}
            {d.versionCount ? ` · v${d.versionCount}` : ''}
            {` · ${compactWhen(d.updatedAt || d.createdAt)}`}
            {!ready && ` · ${d.status}`}
          </span>
        </span>
      </span>
      {ready && (
        <span className="u-cluster" style={{ '--gap': 'var(--sp-1)', flexShrink: 0 }}>
          <Link className="cs-btn cs-btn-ghost cs-btn-sm" to={base} title="Open in the document view">Open</Link>
          {canDownload && (pinned.length ? (
            pinned.map((f) => (
              <button
                key={f.s3Key}
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                data-tip={`Download ${f.name} — exact bytes pinned to this checkpoint${f.sha256 ? ` · SHA-256 ${f.sha256.slice(0, 16)}…` : ''}`}
                onClick={() => downloadAsset({ key: f.s3Key, name: f.name }).catch((err) => toast(err.message, 'danger'))}
              >
                ⬇ {docFileType(d) || 'File'}
              </button>
            ))
          ) : isFileArtifact ? (
            <button type="button" className={`cs-btn cs-btn-sm ${cab ? 'cs-btn-outline' : 'cs-btn-ghost'}`} disabled={!!busy} onClick={grabOriginal} title="Download the file exactly as filed — formatting and tracked changes intact">
              {busy === 'file' ? '…' : `⬇ ${docFileType(d) || 'File'}`}
            </button>
          ) : isDeck ? (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => grab('pptx')} title="Download the latest version as PowerPoint">
              {busy ? '…' : '⬇ PPTX'}
            </button>
          ) : isWorkbook ? (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => grab('xlsx')} title="Download the latest version as Excel (investor house style, formulas recalculate on open)">
              {busy ? '…' : '⬇ XLSX'}
            </button>
          ) : (
            <>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => grab('docx')} title="Download the latest version as Word">
                {busy === 'docx' ? '…' : '⬇ Word'}
              </button>
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => grab('pdf')} title="Download the latest version as PDF">
                {busy === 'pdf' ? '…' : '⬇ PDF'}
              </button>
            </>
          ))}
        </span>
      )}
    </div>
  );
}

// A branch of the tree: a labeled rail hanging off the root, each document
// row attached to the rail with a connector tick — reads as a diagram.
function DocTreeBranch({ scope, deal, label, tip, list, dim, canDownload, toast }) {
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)', position: 'relative', paddingLeft: 22, ...(dim ? { opacity: 0.6 } : {}) }}>
      {/* the branch connector: down from the root's rail, then across */}
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: -4, bottom: 0, borderLeft: '2px solid var(--border)' }} />
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 12, width: 16, borderTop: '2px solid var(--border)' }} />
      <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: '18px' }} data-tip={tip}>
        {label} · {list.length}
      </span>
      <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
        {list.map((d) => (
          <div key={d.id} style={{ position: 'relative', paddingLeft: 18 }}>
            <span aria-hidden="true" style={{ position: 'absolute', left: -22, top: 0, bottom: 0, borderLeft: '2px solid var(--border)' }} />
            <span aria-hidden="true" style={{ position: 'absolute', left: -22, top: '50%', width: 34, borderTop: '1px dashed var(--border)' }} />
            <DocTreeRow scope={scope} deal={deal} d={d} isKey={false} canDownload={canDownload} toast={toast} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DocTreeCard({ scope, deal, docs, draft, onGoTo }) {
  const { can } = useDealPowers(scope.deal);
  const toast = useToast();
  const all = docs || [];
  if (!all.length) return null;
  const branches = Object.fromEntries(TREE_BRANCHES.map((b) => [b.key, []]));
  let root = null;
  for (const d of [...all].sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))) {
    if (draft && d.id === draft.id) root = d;
    else branches[treeBranchOf(d)].push(d);
  }
  const canDownload = can('download');
  return (
    <div className="cs-card u-stack" data-tour="doc-tree" style={{ '--gap': 'var(--sp-3)' }}>
      <SectionHead
        icon="doc"
        tone="info"
        title={`📁 Document tree (${all.length})`}
        guide="document-tree"
        hint="The project's paper as a map — key document at the root, everything else grouped by what it is; one click to open or download the latest version for a meeting"
        action={<button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo('documents')}>Manage documents →</button>}
      />
      <ScrollZone maxHeight={480} className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {root && (
          <div style={{ paddingBottom: 'var(--sp-1)', borderBottom: '1px solid var(--border)' }}>
            <DocTreeRow scope={scope} deal={deal} d={root} isKey canDownload={canDownload} toast={toast} />
          </div>
        )}
        {TREE_BRANCHES.map(({ key, label, tip }) =>
          branches[key].length ? (
            <DocTreeBranch
              key={key}
              scope={scope}
              deal={deal}
              label={label}
              tip={tip}
              list={branches[key]}
              dim={key === 'history'}
              canDownload={canDownload}
              toast={toast}
            />
          ) : null
        )}
      </ScrollZone>
    </div>
  );
}

// ---- Key facts audit — catch the leftover before it ships ------------------------
// Every entity, jurisdiction, date, amount, and address parsed from the
// current draft (docGraph extractFacts). Where one type carries conflicting
// values (Nevada AND Delaware; two registered addresses), the minority
// values are flagged as likely leftovers with the dominant value offered as
// the alternate — click through to the document's facts panel to replace.
const FACT_EMOJI = { entity: '🏢', jurisdiction: '⚖️', date: '📅', amount: '💲', percent: '％', address: '📍' };

function KeyFactsCard({ scope, draft }) {
  const graphPath = draft ? `orgs/${scope.org}/deals/${scope.deal}/docs/${draft.id}/graph/current` : null;
  const { doc: graph } = useDoc(graphPath);
  const facts = graph?.facts || [];
  if (!draft || !facts.length) return null;

  const base = `/deals/${scope.deal}/docs/${draft.id}`;
  const byType = new Map();
  for (const f of facts) {
    if (!byType.has(f.type)) byType.set(f.type, []);
    byType.get(f.type).push(f);
  }
  // Same detector as the DocView review queue (minority values + entity
  // spelling variants), minus anything the user already ruled on.
  const allConflicts = computeFactConflicts(facts, draft.factReviews);
  const conflictByValue = new Map(allConflicts.filter((c) => !c.resolution).map((c) => [c.fact.value, c]));
  const conflicts = [...conflictByValue.values()];

  return (
    <div
      className="cs-card u-stack"
      style={{ '--gap': 'var(--sp-4)', ...(conflicts.length ? { borderLeft: '3px solid var(--warn)' } : {}) }}
    >
      <div className="u-split" style={{ alignItems: 'flex-start' }}>
        <SectionHead
          icon="alert"
          tone={conflicts.length ? 'warn' : 'info'}
          title={`🧾 Key facts audit${conflicts.length ? ` — ${conflicts.length} to review` : ''}`}
          guide="key-facts"
          hint="Every entity, jurisdiction, date, amount, and address parsed from the current draft — ⚠️ marks values that usually shouldn't coexist; the review queue in the document view asks each question with one-click answers"
        />
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0, alignItems: 'center' }}>
          {graph?.versionNumber && (
            <span className="cs-chip u-xs u-tabular" title="The version these facts were parsed from">as of v{graph.versionNumber}</span>
          )}
          <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`${base}?facts=1`}>
            Audit &amp; replace in document
          </Link>
        </span>
      </div>
      {graph?.versionId && draft.currentVersionId && graph.versionId !== draft.currentVersionId && (
        <span className="u-xs" style={{ color: 'var(--warn)' }}>
          ⏳ Facts describe v{graph.versionNumber} — a newer version exists; the graph rebuilds within ~1 minute.
        </span>
      )}
      <ScrollZone maxHeight={440} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 'var(--sp-4)' }}>
        {[...byType.entries()].map(([type, list]) => (
          <div key={type} className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <span className="u-xs u-muted" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {FACT_EMOJI[type] || ''} {FACT_TYPE_LABEL[type] || type} · {list.length}
            </span>
            <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
              {list.slice(0, 6).map((f) => {
                const conflict = conflictByValue.get(f.value);
                return (
                  <Link
                    key={f.value}
                    to={`${base}?facts=1&clause=${(f.clauseIds || [])[0] || ''}`}
                    className="u-small u-split"
                    style={{ textDecoration: 'none', color: 'inherit', gap: 'var(--sp-2)', alignItems: 'baseline' }}
                    title={conflict
                      ? `Conflicts with "${conflict.suggested.value}" — the review queue in the document view asks the exact question and offers one-click answers.`
                      : `Appears in ${(f.clauseIds || []).length} section(s) — click to review`}
                  >
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {conflict && <span aria-hidden="true">⚠️ </span>}
                      {f.value}
                    </span>
                    <span className="u-xs u-muted u-tabular" style={{ flexShrink: 0 }}>
                      ×{f.count}{conflict ? ` → ${conflict.suggested.value.slice(0, 24)}?` : ''}
                    </span>
                  </Link>
                );
              })}
              {list.length > 6 && <span className="u-xs u-muted">+{list.length - 6} more in the document view</span>}
            </div>
          </div>
        ))}
      </ScrollZone>
    </div>
  );
}

// Collapsible deep-dive section — closed it's a slim labeled bar, open it
// mounts its children (lazy: closed sections hold no onSnapshot listeners).
// Open/closed persists per project in localStorage.
function CollapseSection({ dealId, id, title, defaultOpen = false, children }) {
  const storeKey = `fl-dash-open:${dealId}:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storeKey);
      return v === null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem(storeKey, o ? '0' : '1'); } catch { /* nicety */ }
    return !o;
  });
  return (
    <div id={id} className="u-stack dw-anchor" style={{ '--gap': 'var(--sp-4)' }}>
      <button type="button" className={`dw-secbar ${open ? 'open' : ''}`} onClick={toggle} aria-expanded={open}>
        <strong>{title}</strong>
        <span className="dw-secbar-chev"><Icon name="chevronDown" size={16} /></span>
      </button>
      {open && children}
    </div>
  );
}

function DashboardTab({ scope, deal, dealPoints, docs, participant, onGoTo }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { docs: audit } = useEntityList('audit', scope);
  const { docs: comments } = useEntityList('comment', scope);
  // Latest AI run for this project — powers the cockpit button. The org task
  // registry is small (capped history), so client-side filtering is fine.
  const { docs: orgTasks } = useCollection(
    scope ? `orgs/${scope.org}/tasks` : null,
    { where: scope ? ['dealId', '==', scope.deal] : null }
  );
  const dealTasks = [...(orgTasks || [])].sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  const runningTask = dealTasks.find((t) => t.status === 'running') || null;
  const latestTask = runningTask || dealTasks[0] || null;
  // The spotlighted working draft. The marked starting point wins outright;
  // otherwise prefer the negotiated agreement: authored design docs/decks in
  // the same deal must never displace it.
  const draft = useMemo(() => {
    const all = docs || [];
    const usable = (d) => d.status === 'ready' || d.status === 'queued' || d.status === 'processing';
    return all.find((d) => usable(d) && d.id === deal.coreDocId)
      || all.find((d) => usable(d) && d.kind === 'primary' && (docViewOf(d) === 'agreement'))
      || all.find((d) => usable(d) && d.kind === 'primary')
      || all.find(usable)
      || null;
  }, [docs, deal.coreDocId]);
  const { docs: draftVersions } = useEntityList(
    'version',
    scope && draft ? { ...scope, dealDoc: draft.id } : null,
    { enabled: !!(scope && draft) }
  );
  const live = (dealPoints || []).filter((p) => !p.suggested);
  const suggested = (dealPoints || []).filter((p) => p.suggested);
  const agreed = live.filter((p) => p.status === 'agreed');
  const [milestoneLabel, setMilestoneLabel] = useState('');
  const [milestoneDate, setMilestoneDate] = useState('');

  const parties = deal.parties || [];
  const partyColors = Object.fromEntries(parties.map((p, i) => [p.key, PARTY_COLORS[i % PARTY_COLORS.length]]));
  // Authored projects (FDD, deck, memo…) have no negotiation surfaces —
  // the dashboard leads with documents and activity instead.
  const negotiated = isNegotiated(deal) || (dealPoints || []).length > 0;

  const addMilestone = async (e) => {
    e.preventDefault();
    if (!milestoneLabel.trim() || !milestoneDate) return;
    try {
      const milestones = [...(deal.milestones || []), {
        label: milestoneLabel.trim(),
        date: new Date(milestoneDate).getTime(),
        done: false,
      }].sort((a, b) => a.date - b.date);
      await updateEntity('deal', { org: scope.org }, scope.deal, { milestones });
      setMilestoneLabel('');
      setMilestoneDate('');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const toggleMilestone = async (idx) => {
    const milestones = (deal.milestones || []).map((m, i) => (i === idx ? { ...m, done: !m.done } : m));
    await updateEntity('deal', { org: scope.org }, scope.deal, { milestones });
  };

  // ---- Next steps: derived from live state, most urgent first ----
  const nextSteps = [];
  if (suggested.length) nextSteps.push({ label: `Review ${suggested.length} AI-suggested deal point${suggested.length === 1 ? '' : 's'}`, go: 'points' });
  (docs || []).filter((d) => d.status === 'error').forEach((d) => nextSteps.push({ label: `Fix failed import: "${d.title}"`, go: 'documents' }));
  live.filter((p) => p.status === 'escalated').forEach((p) => nextSteps.push({ label: `Resolve escalated: "${p.title}"`, go: 'points' }));
  live
    .filter((p) => p.status === 'open' && (p.priority === 'critical' || p.priority === 'high'))
    .filter((p) => parties.some((pt) => !(p.positions || {})[pt.key]))
    .slice(0, 3)
    .forEach((p) => nextSteps.push({ label: `Collect positions on "${p.title}"`, go: 'points' }));
  (deal.milestones || [])
    .filter((m) => !m.done && m.date > Date.now() - 86400000)
    .slice(0, 2)
    .forEach((m) => nextSteps.push({ label: `${m.label} — ${new Date(m.date).toLocaleDateString()}`, go: null }));

  const issues = live
    .filter((p) => p.status === 'escalated' || (p.status === 'open' && (p.priority === 'critical' || p.priority === 'high')))
    .sort((a, b) => (a.status === 'escalated' ? -1 : 1) - (b.status === 'escalated' ? -1 : 1));

  // Seals fold into the change that produced them (same treatment as the
  // full trail) — a paper trail that's 2/3 "checkpoint sealed" rows says
  // nothing. The timeline covers every event that changed a document's
  // content or custody, not just the version.* types.
  const { merged: auditMerged, sealsFor: auditSeals } = useMemo(() => foldSeals(audit || []), [audit]);
  const timelineTypes = [
    'deal.created', 'doc.uploaded', 'doc.ingested', 'doc.newDraft', 'doc.coreMarked',
    'version.published', 'version.sealed', 'agent.derived', 'agent.vibe', 'agent.created',
    'agent.converted', 'export.docx', 'export.pptx', 'verify.chain',
  ];
  const timeline = auditMerged.filter((ev) => timelineTypes.includes(ev.type)).slice(0, 30);
  const recentComments = (comments || []).sort((a, b) => b.createdAt - a.createdAt).slice(0, 6);

  // Kanban columns use the decision vocabulary (same words as the register's
  // chips). Countered folds into Open on the board; Rejected shares a column
  // with Fallen away — both are "off the table", and the pointer below sends
  // people to the Decisions log for the full record.
  const kanbanCols = [
    { key: 'open', label: DP_DECISION_LABELS.open, match: (d) => d === 'open' || d === 'countered', go: 'open' },
    { key: 'escalated', label: DP_DECISION_LABELS.escalated, match: (d) => d === 'escalated', go: 'escalated' },
    { key: 'accepted', label: DP_DECISION_LABELS.accepted, match: (d) => d === 'accepted', go: 'accepted' },
    { key: 'off', label: 'Rejected · Fallen away', match: (d) => d === 'rejected' || d === 'fallenAway', go: 'rejected' },
  ];
  const weekDecisions = decisionsSince(audit);
  const decisionsPointer = (
    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm u-tabular" onClick={() => onGoTo('points', 'decisions')} data-tip="Every accept / reject / counter on record, with who and when">
      {weekDecisions.accepted} accepted · {weekDecisions.rejected} rejected this week → Decisions
    </button>
  );

  // ---- Right rail ("Needs you" first): populated cards render, empty ones
  // disappear entirely — the rail is the scan-then-act queue, not a wall of
  // placeholders. Milestones always renders (it carries the add form).
  const railCards = [
    {
      key: 'suggestions',
      empty: !suggested.length,
      node: <AiSuggestionsCard scope={scope} participant={participant} suggested={suggested} onGoTo={onGoTo} />,
    },
    {
      key: 'next',
      empty: !nextSteps.length,
      node: (
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <SectionHead icon="steps" tone="ok" title="Next steps" hint="What to do now — derived from live state, most urgent first" />
          {nextSteps.slice(0, 7).map((s, i) =>
            s.go ? (
              <button key={i} type="button" className="u-split u-small cs-rowlink" onClick={() => onGoTo(s.go)}>
                <span>{s.label}</span>
                <span className="u-muted">→</span>
              </button>
            ) : (
              <div key={i} className="u-split u-small">
                <span>{s.label}</span>
              </div>
            )
          )}
        </div>
      ),
    },
    ...(negotiated ? [{
      key: 'issues',
      empty: !issues.length,
      node: (
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <SectionHead icon="alert" tone="danger" title="Issues needing attention" hint="Escalations and high-priority open points — resolve or reassign" />
          {issues.slice(0, 7).map((p) => (
            <button key={p.id} type="button" className="u-split u-small cs-rowlink" onClick={() => onGoTo('points', p.status === 'escalated' ? 'escalated' : 'open')} title="Open in the register">
              <span>
                <span className={`cs-badge ${p.status === 'escalated' ? 'b-danger' : PRIORITY_TONE[p.priority] || 'b-warn'}`} style={{ marginRight: 8 }}>
                  {p.status === 'escalated' ? 'escalated' : p.priority}
                </span>
                {p.title}
              </span>
              <span className="u-muted">→</span>
            </button>
          ))}
          {weekDecisions.total > 0 && (
            <button type="button" className="u-split u-small cs-rowlink u-muted" onClick={() => onGoTo('points', 'decisions')} title="Open the decisions log">
              <span>{weekDecisions.accepted} accepted · {weekDecisions.rejected} rejected this week</span>
              <span>→ Decisions</span>
            </button>
          )}
        </div>
      ),
    }] : []),
    {
      key: 'timeline',
      empty: !timeline.length,
      node: (
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <SectionHead
            icon="clock"
            tone="info"
            title="Document timeline"
            guide="timeline"
            hint="The paper trail — every upload, new version, seal, and export, newest first"
            action={<button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo('activity')}>Full audit trail →</button>}
          />
          <ScrollZone maxHeight={420} className="cs-timeline">
            {timeline.map((ev) => (
              <AuditMiniRow
                key={ev.id}
                ev={ev}
                seals={auditSeals.get(ev.id)}
                className="cs-timeline-item"
                onClick={() => {
                  if (ev.meta?.docId) {
                    navigate(`/deals/${scope.deal}/docs/${ev.meta.docId}${ev.meta.versionId ? `?v=${ev.meta.versionId}` : ''}`);
                  } else {
                    onGoTo('activity');
                  }
                }}
              />
            ))}
          </ScrollZone>
        </div>
      ),
    },
    {
      key: 'comments',
      empty: !recentComments.length,
      node: (
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <SectionHead icon="comment" tone="ok" title="Recent comments" hint="Latest discussion across clauses and deal points" />
          {recentComments.map((c) => (
            <button
              key={c.id}
              type="button"
              className="u-small cs-rowlink"
              title={c.anchor?.docId ? 'Open where this was said' : 'Open the deal points register'}
              onClick={() => {
                const a = c.anchor || {};
                if (a.docId) {
                  navigate(`/deals/${scope.deal}/docs/${a.docId}${a.clauseId ? `?clause=${a.clauseId}` : ''}`);
                } else if (a.kind === 'dealPoint') {
                  onGoTo('points');
                } else {
                  onGoTo('activity');
                }
              }}
            >
              <strong>{c.authorName}</strong>
              {c.partyKey && <span className="u-muted"> ({c.partyKey})</span>}
              <span className="u-muted u-xs"> · {new Date(c.createdAt).toLocaleString()}</span>
              <div className="u-muted" style={{ marginTop: 2 }}>{String(c.body).slice(0, 160)}</div>
            </button>
          ))}
        </div>
      ),
    },
    {
      key: 'milestones',
      empty: !(deal.milestones || []).length,
      node: (
        <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <SectionHead icon="flag" tone="warn" title="Milestones" hint={negotiated ? 'Key dates on the road to signing — check them off as they land' : 'Key dates and deadlines — check them off as they land'} />
          {!(deal.milestones || []).length && <span className="u-small u-muted">No milestones — add the target signing date.</span>}
          {(deal.milestones || []).map((m, i) => (
            <label key={`${m.label}-${m.date}`} className="u-split u-small" style={{ cursor: 'pointer' }}>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                <input type="checkbox" checked={!!m.done} onChange={() => toggleMilestone(i)} />
                <span style={m.done ? { textDecoration: 'line-through', color: 'var(--muted)' } : undefined}>{m.label}</span>
              </span>
              <span className="u-muted u-tabular">{new Date(m.date).toLocaleDateString()}</span>
            </label>
          ))}
          <form className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} onSubmit={addMilestone}>
            <input className="cs-input" style={{ flex: 1, minWidth: 140 }} value={milestoneLabel} onChange={(e) => setMilestoneLabel(e.target.value)} placeholder="Target signing" aria-label="Milestone label" />
            <input className="cs-input" type="date" value={milestoneDate} onChange={(e) => setMilestoneDate(e.target.value)} aria-label="Milestone date" />
            <button type="submit" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!milestoneLabel.trim() || !milestoneDate}>Add</button>
          </form>
        </div>
      ),
    },
  ];
  // Rail shows populated cards only; milestones stays (it carries the add form).
  const railVisible = railCards.filter((c) => !c.empty || c.key === 'milestones');

  // ---- Job cards + jump chips inputs ----
  const primariesCount = (docs || []).filter((d) => d.kind === 'primary' && !isSupersededDoc(d)).length;
  const vAllTop = draftVersions || [];
  const vSealedTop = vAllTop.filter((v) => v.sealed && v.checkpoint).length;
  const draftBase = draft ? `/deals/${scope.deal}/docs/${draft.id}` : null;
  const openLive = live.filter((p) => p.status === 'open').length;
  const escalatedLive = live.filter((p) => p.status === 'escalated').length;

  // On-this-page navigator — built from the sections actually rendered.
  const jump = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    try { window.history.replaceState(null, '', `#${id}`); } catch { /* nicety */ }
  };
  const jumpSections = [
    { id: 'doc-tree', label: 'Documents' },
    draft && { id: 'draft', label: 'Draft' },
    negotiated && { id: 'kanban', label: 'Deal points' },
    { id: 'deep-dive', label: 'Structure & facts' },
    railVisible.length > 0 && { id: 'needs-you', label: 'Needs you' },
  ].filter(Boolean);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <SpotlightTour
        tourId="dashboard"
        steps={[
          { target: '[data-tour="doc-tree"]', title: '📁 Your documents, meeting-ready', body: 'The key document sits at the root; every artifact is grouped underneath with one-click downloads — always the latest version. Uploaded originals download byte-exact, tracked changes included.' },
          { target: '[data-tour="deal-points"]', title: '⚖️ Every open issue, tracked', body: 'The deal points board shows where each negotiated issue stands and each party\u2019s stance. It updates as new drafts and correspondence are ingested.' },
          { target: '[data-tour="project-ask"]', title: 'Ask, instruct & add', body: 'This panel lives on the project. Ask the project brain, instruct the drafting agent, or drop a new document in — nothing leaves these files. Press ⌘K to focus it from anywhere in the project.' },
          { target: '.gd-navtrigger', title: '📖 The guide is always here', body: 'Every card has a small ? that explains it. This button opens the full guide — or just ask the assistant in your own words.' },
        ]}
      />
      {/* On-this-page navigator — sticky jump chips over the sections that
          actually rendered (most cards return null when empty). */}
      {jumpSections.length > 1 && (
        <nav className="dw-jump" aria-label="On this page">
          <span className="dw-jump-label">Jump to</span>
          {jumpSections.map((s) => (
            <button key={s.id} type="button" className="dw-jump-chip" onClick={() => jump(s.id)}>
              {s.label}
            </button>
          ))}
        </nav>
      )}

      {/* Job cards — the four places users actually go, one click each. The
          old stat strip folds into these as sublabels. */}
      <div className="dw-jobs">
        {draft ? (
          <Link className="dw-job" to={`${draftBase}?mode=clean`}>
            <span className="dw-job-icon"><Icon name="doc" size={20} /></span>
            <strong>Open the draft</strong>
            <span className="dw-job-sub">
              {vAllTop.length
                ? `v${Math.max(...vAllTop.map((v) => v.number || 0))} · ⛓ ${vSealedTop}/${vAllTop.length} sealed`
                : draft.title}
            </span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </Link>
        ) : (
          <button type="button" className="dw-job" onClick={() => onGoTo('documents')}>
            <span className="dw-job-icon"><Icon name="upload" size={20} /></span>
            <strong>Add documents</strong>
            <span className="dw-job-sub">Upload or paste a draft — it is ingested, graphed, and indexed here</span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </button>
        )}
        {draft && vAllTop.length > 1 ? (
          <Link className="dw-job blue" to={`${draftBase}?mode=redline`}>
            <span className="dw-job-icon"><Icon name="compare" size={20} /></span>
            <strong>Review what moved</strong>
            <span className="dw-job-sub">Redline of the latest version against the previous, attributed by party</span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </Link>
        ) : (
          <button type="button" className="dw-job blue" onClick={() => onGoTo('documents')}>
            <span className="dw-job-icon"><Icon name="compare" size={20} /></span>
            <strong>Review what moved</strong>
            <span className="dw-job-sub">Upload the counterparty's turn — tracked changes become a reviewable redline</span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </button>
        )}
        {negotiated ? (
          <button type="button" className="dw-job purple" onClick={() => onGoTo('points')}>
            <span className="dw-job-icon"><Icon name="list" size={20} /></span>
            <strong>Deal points</strong>
            <span className="dw-job-sub">
              {suggested.length && !live.length
                ? `${suggested.length} AI-suggested await review`
                : `${openLive} open · ${escalatedLive} escalated · ${agreed.length} agreed${suggested.length ? ` · ${suggested.length} suggested` : ''}`}
            </span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </button>
        ) : (
          <button type="button" className="dw-job purple" onClick={() => openCommandBar()}>
            <span className="dw-job-icon"><Icon name="sparkle" size={20} /></span>
            <strong>Ask the project</strong>
            <span className="dw-job-sub">Answers from this project's brain, with citations — nothing leaves these files</span>
            <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
          </button>
        )}
        <button type="button" className="dw-job green" onClick={() => onGoTo('documents')}>
          <span className="dw-job-icon"><Icon name="folder" size={20} /></span>
          <strong>Send a package</strong>
          <span className="dw-job-sub">
            {primariesCount} document{primariesCount === 1 ? '' : 's'} — clean copy, redline, and open points in one download
          </span>
          <span className="dw-job-go"><Icon name="arrowRight" size={16} /></span>
        </button>
      </div>

      <div className="dw-grid">
      <div className="dw-main">

      {/* Document tree — the meeting-prep surface, right at the top: key
          document at the root, everything else on content-aware branches
          (negotiation record, filings, exhibits, references, history) with
          latest version + direct download on every row. */}
      <div id="doc-tree" className="dw-anchor">
        <DocTreeCard scope={scope} deal={deal} docs={docs} draft={draft} onGoTo={onGoTo} />
      </div>

      {/* Current draft — the fastest path into the document, with the mini
          version timeline right here so "where things are at" is one glance. */}
      {draft && (() => {
        const base = `/deals/${scope.deal}/docs/${draft.id}`;
        // Immutability demarcation: how much of this document's version chain
        // is sealed with checkpoint hashes. Sealed = SHA-256 chained + S3
        // archived; the checkpoint worker seals new versions within ~1 min.
        const vAll = draftVersions || [];
        const vSealed = vAll.filter((v) => v.sealed && v.checkpoint);
        const lastSealed = [...vSealed].sort((a, b) => b.number - a.number)[0];
        return (
          <div id="draft" className="cs-card u-stack dw-anchor" style={{ '--gap': 'var(--sp-4)', borderLeft: '3px solid var(--surf)' }}>
            <div className="u-split" style={{ alignItems: 'center', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'center', minWidth: 0, flexWrap: 'nowrap' }}>
                <DocThumb doc={draft} deal={deal} to={base} size={52} />
                <span className="u-stack" style={{ '--gap': '1px', minWidth: 0 }}>
                  <span className="u-xs u-muted" style={{ letterSpacing: '0.08em' }}>CURRENT DRAFT</span>
                  <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
                    <strong>{draft.title}</strong>
                    {draft.id === deal.coreDocId && <span className="cs-badge b-info">★ starting point</span>}
                    {vAll.length > 0 && (
                      <span
                        className={`cs-chip u-xs u-tabular ${vSealed.length === vAll.length ? '' : 'u-muted'}`}
                        data-tip={
                          vSealed.length === vAll.length
                            ? `Every version is sealed with a chained SHA-256 checkpoint and archived to S3. Latest chain hash: ${lastSealed?.checkpoint?.chainHash?.slice(0, 16)}… Verify the full chain from the document view.`
                            : `${vAll.length - vSealed.length} version(s) awaiting seal — the checkpoint worker seals within ~1 min`
                        }
                        style={vSealed.length === vAll.length ? { color: 'var(--ok)', borderColor: 'var(--ok)' } : undefined}
                      >
                        {vSealed.length === vAll.length
                          ? `⛓ ${vAll.length}/${vAll.length} sealed`
                          : `⛓ ${vSealed.length}/${vAll.length} sealed`}
                      </span>
                    )}
                  </span>
                  <span className="u-xs u-muted u-tabular">
                    v{draft.versionCount || 0} · updated {compactWhen(draft.updatedAt || draft.createdAt)}
                  </span>
                </span>
              </span>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                <Link className="cs-btn cs-btn-primary" to={`${base}?mode=clean`}>Open draft</Link>
                <Link className="cs-btn cs-btn-outline" to={`${base}?mode=redline`}>Redline</Link>
                <Link className="cs-btn cs-btn-outline" to={`${base}?mode=sxs`}>Clean vs redline</Link>
                <Link className="cs-btn cs-btn-outline" to={`${base}/compliance`}>Jurisdictions</Link>
                {latestTask && (
                  <Link
                    className={`cs-btn ${runningTask ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                    to={`/activity/${latestTask.id}`}
                    title={runningTask ? 'An AI run is live right now — watch it think' : 'Replay the latest AI run — graph, retrievals, and edits'}
                  >
                    {runningTask ? '● Live cockpit' : 'Cockpit'}
                  </Link>
                )}
              </span>
            </div>
            <VersionRail base={base} doc={draft} versions={draftVersions} partyColors={partyColors} />
          </div>
        );
      })()}

      {/* No draft yet but an AI run exists (e.g. still deriving) — the cockpit
          button must not disappear with the card it usually lives in. */}
      {!draft && latestTask && (
        <div className="cs-card u-split" style={{ alignItems: 'center' }}>
          <span className="u-small u-muted">
            {runningTask ? 'An AI run is in progress on this project.' : 'Latest AI run on this project.'}
          </span>
          <Link className={`cs-btn ${runningTask ? 'cs-btn-primary' : 'cs-btn-outline'}`} to={`/activity/${latestTask.id}`}>
            {runningTask ? '● Live cockpit' : 'Replay in cockpit'}
          </Link>
        </div>
      )}

      {/* Kanban — negotiated projects only */}
      {negotiated && (
      <div id="kanban" className="u-stack dw-anchor" data-tour="deal-points" style={{ '--gap': 'var(--sp-3)' }}>
        <SectionHead
          icon="board"
          tone="info"
          title="Deal points board"
          guide="deal-points"
          hint="Where every negotiated issue stands — open, escalated, accepted, rejected or fallen away. AI suggestions only land here once you accept them."
          action={
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              {decisionsPointer}
              {suggested.length > 0 && (
                <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onGoTo('points')}>
                  Review {suggested.length} suggested →
                </button>
              )}
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo('points')}>Open register →</button>
            </span>
          }
        />
        <div className="cs-kanban">
          {kanbanCols.map((col) => {
            const cards = live.filter((p) => col.match(decisionOf(p)));
            return (
              <div key={col.key} className="cs-kanban-col">
                <button type="button" className="cs-kanban-col-head cs-rowlink" onClick={() => onGoTo('points', col.go)} title="Open the register filtered to this slice">
                  <span>{col.label}</span>
                  <span className="u-tabular">{cards.length}</span>
                </button>
                {cards.slice(0, 8).map((p) => (
                  <KanbanCard key={p.id} point={p} parties={parties} partyColors={partyColors} onOpen={() => onGoTo('points', col.go)} />
                ))}
                {cards.length > 8 && (
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onGoTo('points', col.go)}>+{cards.length - 8} more</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Deep dive — the analysis surfaces: heavyweight, reference-shaped,
          and collapsible so the scan-then-act path above stays short. Closed
          sections don't mount (no idle onSnapshot listeners). */}
      <CollapseSection dealId={scope.deal} id="deep-dive" title="Deep dive — structure, objectives, key facts & key information">
        {/* Company structure — the visual entity/agreement map every targeted
            AI task works from. */}
        <StructureMapCard scope={scope} deal={deal} participant={participant} />

        {/* Objectives + purpose audit — the project structure, each agreement's
            role, and how far any draft is from its purpose (relic/misplaced
            detector across the agreement ecosystem). */}
        <ObjectiveCard scope={scope} deal={deal} docs={docs} draft={draft} />

        {/* Key facts audit — parsed entities/jurisdictions/dates/amounts/
            addresses with conflict flags (the repurposing-leftover catcher). */}
        <KeyFactsCard scope={scope} draft={draft} />

        {/* Key information — entities, roles, governing law, defined terms. */}
        <KeyInfoCard scope={scope} docs={docs} draft={draft} />

        {deal.description && (
          <div className="cs-card u-small" style={{ lineHeight: 1.6 }}>
            {deal.description}
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
              {parties.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className="cs-chip"
                  style={{ borderColor: partyColors[p.key], cursor: 'pointer' }}
                  onClick={() => onGoTo('team')}
                  title="See who is on each side — Team tab"
                >
                  <span className="cs-sidebar-dot" style={{ background: partyColors[p.key], display: 'inline-block', marginRight: 6 }} />
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </CollapseSection>

      </div>{/* /dw-main */}

      {/* Right rail — the scan-then-act queue: what needs YOU first (agent
          questions, AI suggestions, counsel turns), then next steps, issues,
          the activity mini-timeline, comments, and milestones. */}
      <aside id="needs-you" className="dw-rail dw-anchor" aria-label="Needs you">
        <AgentQuestionsCard scope={scope} deal={deal} participant={participant} docs={docs} />
        <CounselTurnsCard audit={audit} onGoTo={onGoTo} />
        {railVisible.map((c) => <React.Fragment key={c.key}>{c.node}</React.Fragment>)}
      </aside>
      </div>{/* /dw-grid */}
    </div>
  );
}

// ---- The workspace --------------------------------------------------------------------
export default function DealWorkspace() {
  const { dealId } = useParams();
  const { orgId } = useOrg();
  const { user } = useAuth();
  const scope = useMemo(() => (orgId ? { org: orgId, deal: dealId } : null), [orgId, dealId]);

  const { doc: deal } = useEntityDoc('deal', orgId ? { org: orgId } : null, orgId ? dealId : null);
  const { docs: dealPoints } = useEntityList('dealPoint', scope, { enabled: !!scope });
  const { docs: docs } = useEntityList('dealDoc', scope, { enabled: !!scope });
  const { docs: participants } = useEntityList('participant', scope, { enabled: !!scope });

  const participant = (participants || []).find((p) => p.uid === user?.uid) || null;

  // The tab lives in the URL (?tab=) so back/refresh/shared links land on the
  // right tab, and goTo() can also point at a dashboard section (#hash) that
  // the effect below scrolls to once it renders.
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [projectTagsOpen, setProjectTagsOpen] = useState(false);
  const tab = searchParams.get('tab') || 'dashboard';
  const goTo = (tabId, sectionId) => {
    navigate({ search: `?tab=${tabId}`, hash: sectionId ? `#${sectionId}` : '' });
  };

  useEffect(() => {
    if (orgId && dealId && deal) recordRecentDeal(orgId, dealId);
  }, [orgId, dealId, deal]);

  // Scroll to the section hash once its card is on screen. Dashboard cards
  // mount async (onSnapshot data), so retry briefly instead of racing them.
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.slice(1);
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      tries += 1;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearInterval(timer);
      } else if (tries > 20) {
        clearInterval(timer);
      }
    }, 150);
    return () => clearInterval(timer);
  }, [location.hash, tab]);

  if (!orgId || deal === undefined) {
    return <div className="cs-page"><div className="cs-skeleton" style={{ height: 240 }} /></div>;
  }
  if (deal === null) {
    return (
      <div className="cs-page">
        <div className="cs-banner b-danger">This project is not in your list — you can only open projects you have been added to.</div>
      </div>
    );
  }

  const livePoints = (dealPoints || []).filter((p) => !p.suggested);
  const openCount = livePoints.filter((p) => p.status === 'open' || p.status === 'escalated').length;
  const acceptedCount = livePoints.filter((p) => decisionOf(p) === 'accepted').length;
  const suggestedCount = (dealPoints || []).filter((p) => p.suggested).length;
  // The tab label keeps the decided count visible so accepting a batch
  // reads as progress ("3 open · 9 accepted"), not as points vanishing.
  const pointsLabel =
    openCount === 0 && suggestedCount > 0
      ? `Deal points (${suggestedCount} to review)`
      : `Deal points (${openCount} open${acceptedCount ? ` · ${acceptedCount} accepted` : ''}${suggestedCount ? `, ${suggestedCount} suggested` : ''})`;

  const ptype = projectTypeOf(deal);
  // Authored projects (FDDs, decks, memos…) are document-first — the deal
  // points register only appears if points actually exist (e.g. converted
  // projects), never as a default surface.
  const showPoints = ptype.negotiated || (dealPoints || []).length > 0;

  return (
    <div className="cs-page u-stack m-rise" style={{ '--gap': 'var(--sp-5)' }}>
      <div className="u-small u-muted"><Link to="/projects">Projects</Link> / {deal.name}</div>
      <div className="cs-page-head">
        <div>
          <h1>{deal.name}</h1>
          <p className="u-muted u-small">
            {ptype.icon} {ptype.label}
            {deal.dealType ? ` · ${deal.dealType}` : ''} · {statusLabel(deal)}
            {(deal.parties || []).length > 0 && ` · ${(deal.parties || []).map((p) => p.label).join(' · ')}`}
            {(deal.memberUids || []).includes(user?.uid) && (deal.tags || []).length > 0 && (
              <>
                {' · '}
                {(projectTagsOpen ? deal.tags : deal.tags.slice(0, 4)).map((t) => `#${t}`).join(' ')}
                {deal.tags.length > 4 && (
                  <button
                    type="button"
                    className="cs-chip"
                    style={{ marginLeft: 8, padding: '1px 8px', cursor: 'pointer' }}
                    onClick={() => setProjectTagsOpen((o) => !o)}
                  >
                    {projectTagsOpen ? 'fewer' : `+${deal.tags.length - 4}`}
                  </button>
                )}
              </>
            )}
            {participant && ptype.negotiated && ` — you are ${participant.partyKey} (${participant.dealRole})`}
          </p>
        </div>
        <select
          className="cs-input"
          value={deal.status}
          onChange={(e) => updateEntity('deal', { org: orgId }, dealId, { status: e.target.value })}
          aria-label="Project status"
        >
          {['draft', 'negotiating', 'agreed', 'executed', 'closed'].map((s) => (
            <option key={s} value={s}>{statusLabel(deal, s)}</option>
          ))}
        </select>
      </div>

      <CommandPanel embedded collapsible orgId={orgId} dealId={dealId} dealName={deal.name} deal={deal} participant={participant} />

      <Tabs
        active={tab}
        onChange={(id) => goTo(id)}
        tabs={[
          { id: 'dashboard', label: 'Dashboard', render: () => <DashboardTab scope={scope} deal={deal} dealPoints={dealPoints} docs={docs} participant={participant} onGoTo={goTo} /> },
          { id: 'documents', label: `Documents (${(docs || []).length})`, render: () => <DocumentsTab scope={scope} deal={deal} participant={participant} /> },
          ...(showPoints
            ? [{ id: 'points', label: pointsLabel, render: () => <DealPointsRegister scope={scope} deal={deal} participant={participant} /> }]
            : []),
          { id: 'team', label: 'Team', render: () => <TeamTab scope={scope} deal={deal} participant={participant} /> },
          { id: 'shares', label: 'Shares', render: () => <SharesTab scope={scope} /> },
          { id: 'knowledge', label: 'Knowledge', render: () => <KnowledgeTab scope={scope} /> },
          { id: 'activity', label: 'Activity', render: () => <ActivityTab scope={scope} /> },
        ]}
      />
    </div>
  );
}
